import type { QuoteSourceFragment } from '../../../domain/quoteSourceFragment';
import { purchasePriceSnapshotCents } from '../../../quote-integration/purchasePriceSnapshot';
import { catalogDeviceStock } from '../mock/pricing';
import { createProductCatalog } from '../mock/productCatalog';
import {
  isQuoteDefinitionBound,
  QUOTE_SOURCE_FIELDS,
} from './businessFieldDirectory';
import {
  activeBusinessModes,
  businessModeLabel,
  DEFAULT_QUOTE_BUSINESS_MODE,
  defaultQuoteDiscountForBusinessMode,
  isStableBusinessModeCode,
  requireActiveBusinessMode,
} from './businessModes';
import {
  bindLatestSelectedCalculations,
  freezeSelectedCalculationFacts,
  selectedCalculationKeys,
} from './calculationLifecycle';
import {
  requireDeviceSku,
  deviceFieldSnapshotForQuoteItem as snapshotDeviceMaster,
} from './deviceCatalog';
import { hasDiscountPercentPrecision } from './discountPrecision';
import { freezePublicQuotationProjections } from './frozenQuotationProjection';
import { quoteOutputBusinessSections } from './outputTerms';
import {
  activeRoleIdForState,
  canEditQuoteSourceField,
  canViewQuoteSourceField,
  quotePermissionContext,
  resolveDisposablePermissionAction,
} from './permissionPolicy';
import { isReceiptCapableShareItem } from './publicShareSnapshot';
import {
  buildQuoteChannelProjection,
  shareFieldCombinationSnapshot,
} from './quoteChannelProjection';
import { snapshotQuoteColumns } from './quoteColumns';
import { quoteNewSourceChildChain } from './quoteContainerInsertion';
import {
  quoteContainerEquipmentSourceRevision,
  quoteContainerSourceMemberKey,
} from './quoteContainerSourceIdentity';
import {
  assertQuoteFieldMutationAllowed,
  canProjectGovernedQuoteField,
  type QuoteFieldAuthorization,
} from './quoteFieldPolicy';
import { quoteFieldSelections } from './quoteFieldSelection';
import { captureQuoteSourceFieldSnapshots } from './quoteFieldSnapshots';
import { quoteExclusiveLineCents } from './quoteLocalPricing';
import { quoteItemEffectiveDiscount } from './quotePackageGroupPolicy';
import {
  assertFragmentMemberCoverage,
  assertQuoteFragmentInstances,
  resizeQuoteFragmentGroup,
} from './quoteSourceFragments';
import { quoteTableGroupKey } from './quoteTableGroups';
import {
  assertDiscountPercent,
  assertQuoteQuantity,
  createPricingBaseSnapshot,
  lineTotal,
  occurrencePricingView,
  occurrenceSourceLineCents,
  packageMonthlySubtotal,
  planProductPrice,
  planProductSubtotal,
  planSubtotal,
  quoteAmounts,
  repriceQuoteItem,
} from './quoteTotals';
import {
  createDefaultShareStyles,
  createShareStyleSnapshot,
} from './shareStyles';
import type {
  AuditRecord,
  BusinessModeCode,
  CpqState,
  ProductQuoteStatus,
  Quote,
  QuoteCalculationBinding,
  QuoteDetailTab,
  QuoteItem,
  QuoteSelectionDevice,
  QuoteStatus,
  QuoteVersionGenerationSource,
  QuoteVersionWorkflowStage,
  ReceiptSelectionItemSnapshot,
  ReceiptSelectionSnapshot,
} from './types';
import type { UnifiedFieldValuePort } from './unifiedFieldPort';
import { createDisposableFieldValuePort } from './unifiedFieldPort';

const DEMO_TIME = '2026-08-11T10:00:00.000Z';
const PRODUCT_CATALOG_SEED = createProductCatalog();
const DEFAULT_QUOTE_FIELD_KEYS = PRODUCT_CATALOG_SEED.quoteFieldPool
  .filter((entry) => entry.enabled && entry.defaultVisible)
  .sort((left, right) => left.sortOrder - right.sortOrder)
  .map((entry) => entry.fieldKey);

function seedFieldSelection(fieldKey: string) {
  const entry = PRODUCT_CATALOG_SEED.quoteFieldPool.find(
    (candidate) => candidate.fieldKey === fieldKey && candidate.enabled,
  );
  return entry ? { fieldKey, placement: entry.placement } : undefined;
}

const STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: '草稿',
  collaboration: '协作中',
  customer_confirmation: '对客确认中',
  pending_submission: '待提交',
  submitting: '提交中',
  success: '成功',
  failed: '失败',
  void: '已作废',
  archived: '已归档',
};

const ALLOWED_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  draft: [
    'collaboration',
    'customer_confirmation',
    'pending_submission',
    'void',
  ],
  collaboration: ['customer_confirmation', 'pending_submission', 'void'],
  customer_confirmation: ['collaboration', 'pending_submission', 'void'],
  pending_submission: ['submitting', 'void'],
  submitting: ['pending_submission', 'success', 'failed'],
  success: ['archived'],
  failed: ['pending_submission', 'void'],
  void: ['archived'],
  archived: [],
};

export const quoteStatusLabels = STATUS_LABELS;

export const productQuoteStatusLabels: Record<ProductQuoteStatus, string> = {
  draft: '草稿',
  shared: '已分享',
  confirmed: '最终已确认',
  ordered: '已下单',
  ordered_changing: '已下单·报价变更中',
  voided: '已作废',
  archived: '已归档',
};

export function productStatusForQuote(quote: Quote): ProductQuoteStatus {
  if (quote.status === 'archived') return 'archived';
  if (quote.status === 'void') return 'voided';
  if (quote.status === 'success') return 'ordered';
  if (
    quote.salesOrderId &&
    (quote.status === 'collaboration' ||
      (quote.status === 'draft' && quote.sourceQuoteId))
  ) {
    return 'ordered_changing';
  }
  if (quote.status === 'pending_submission' || quote.status === 'submitting') {
    return 'confirmed';
  }
  if (
    quote.status === 'collaboration' ||
    quote.status === 'customer_confirmation'
  ) {
    return 'shared';
  }
  if (quote.status === 'failed') return 'confirmed';
  return 'draft';
}

function cloneState(state: CpqState): CpqState {
  return structuredClone(state);
}

function deviceFieldSnapshotForQuoteItem(
  device: QuoteSelectionDevice,
): NonNullable<QuoteItem['deviceFieldSnapshot']> {
  return snapshotDeviceMaster(device);
}

function quoteEditContainerChain(
  previousQuote: Quote,
  nextQuote: Quote,
  containerInstanceId: string,
): NonNullable<Quote['containerInstances']> | undefined {
  if (previousQuote.containerEditMode !== 'quote_edit') return undefined;
  const previousById = new Map(
    (previousQuote.containerInstances ?? []).map((entry) => [entry.id, entry]),
  );
  const nextById = new Map(
    (nextQuote.containerInstances ?? []).map((entry) => [entry.id, entry]),
  );
  const chain: NonNullable<Quote['containerInstances']> = [];
  const visited = new Set<string>();
  let currentId: string | null = containerInstanceId;
  while (currentId) {
    if (visited.has(currentId)) return undefined;
    visited.add(currentId);
    const previous = previousById.get(currentId);
    const next = nextById.get(currentId);
    if (
      !previous ||
      !next ||
      previous.kind !== next.kind ||
      previous.originKind !== next.originKind ||
      previous.originId !== next.originId ||
      previous.originRevisionId !== next.originRevisionId ||
      previous.originResourceVersion !== next.originResourceVersion ||
      previous.parentInstanceId !== next.parentInstanceId ||
      previous.parentMemberId !== next.parentMemberId ||
      JSON.stringify(previous.originSnapshot) !==
        JSON.stringify(next.originSnapshot) ||
      next.status !== 'customized'
    )
      return undefined;
    chain.push(next);
    currentId = previous.parentInstanceId;
  }
  const leaf = chain[0];
  if (leaf?.kind !== 'package') return undefined;
  if (chain.some((entry) => entry.kind !== 'package' && entry.kind !== 'plan'))
    return undefined;
  return chain;
}

function isQuoteEditCustomizedRow(
  previousQuote: Quote,
  nextQuote: Quote,
  previousItem: QuoteItem,
  nextItem: QuoteItem,
  newSourceChain?: NonNullable<Quote['containerInstances']>,
): boolean {
  const containerInstanceId = previousItem.containerInstanceId;
  if (
    !containerInstanceId ||
    nextItem.containerInstanceId !== containerInstanceId ||
    nextItem.sourceFragment?.mode !== 'quote-customized' ||
    nextItem.fragmentAllocation !== undefined
  )
    return false;
  const chain =
    quoteEditContainerChain(previousQuote, nextQuote, containerInstanceId) ??
    newSourceChain;
  const leaf = chain?.[0];
  const fragment = nextItem.sourceFragment;
  let relationQuantity = nextItem.packageRelationQuantitySnapshot;
  if (
    leaf?.originKind === 'quote-local-package' &&
    nextItem.packageRelationQuantitySnapshot === undefined &&
    Number.isSafeInteger(leaf.quantity) &&
    leaf.quantity > 0 &&
    Number.isSafeInteger(nextItem.quantity) &&
    nextItem.quantity > 0 &&
    nextItem.quantity % leaf.quantity === 0
  ) {
    const sourceRevisionId = quoteContainerEquipmentSourceRevision(nextItem);
    let sourceMemberKey: string | undefined;
    try {
      sourceMemberKey = quoteContainerSourceMemberKey(nextItem);
    } catch {
      sourceMemberKey = undefined;
    }
    const frozenMembers = leaf.originSnapshot.members.filter(
      (member) =>
        member.sourceKind === 'equipment' &&
        member.sourceId === nextItem.deviceId &&
        (member.sourceRevisionId === sourceRevisionId ||
          (member.sourceRevisionId === null &&
            leaf.originRevisionId === null &&
            member.resourceVersion === leaf.originSnapshot.resourceVersion &&
            leaf.originResourceVersion ===
              leaf.originSnapshot.resourceVersion)) &&
        (member.memberId === nextItem.deviceId ||
          member.memberId === `quote-local-member:${nextItem.deviceId}` ||
          member.memberId === sourceMemberKey ||
          member.memberId ===
            `quote-local-member:${leaf.originId}:${nextItem.deviceId}`) &&
        Number.isSafeInteger(member.quantity) &&
        member.quantity > 0,
    );
    const frozenMember = frozenMembers[0];
    const sourceWasComplete = previousItem.sourceFragment?.mode === 'complete';
    const previousLeaf = previousQuote.containerInstances?.find(
      (instance) => instance.id === leaf.id && instance.kind === 'package',
    );
    const isNewMemberOfExistingLocalPackage =
      leaf.originKind === 'quote-local-package' &&
      previousQuote.containerEditMode === 'quote_edit' &&
      previousQuote.containerInstances?.some(
        (instance) =>
          instance.id === leaf.id &&
          instance.kind === 'package' &&
          instance.originKind === 'quote-local-package',
      ) === true &&
      !previousQuote.items.some((item) => item.itemId === nextItem.itemId) &&
      newSourceChain === undefined &&
      nextItem.sourceType === 'device' &&
      nextItem.sourceId === nextItem.deviceId &&
      Boolean(sourceRevisionId) &&
      sourceMemberKey ===
        `equipment:${nextItem.deviceId}:revision:${sourceRevisionId}`;
    let previousMemberKey: string | undefined;
    try {
      previousMemberKey = quoteContainerSourceMemberKey(previousItem);
    } catch {
      previousMemberKey = undefined;
    }
    const isPreviouslySavedLocalMember =
      leaf.originKind === 'quote-local-package' &&
      previousQuote.containerEditMode === 'quote_edit' &&
      previousLeaf?.originKind === 'quote-local-package' &&
      previousItem.containerInstanceId === leaf.id &&
      previousItem.sourceFragment?.mode === 'quote-customized' &&
      previousItem.fragmentAllocation === undefined &&
      previousItem.sourceFragment.instanceKey === leaf.id &&
      previousItem.sourceFragment.instanceQuantity === previousLeaf.quantity &&
      previousItem.sourceFragment.fragmentKey ===
        `quote-row:${previousItem.itemId}` &&
      previousItem.sourceFragment.memberKey === previousMemberKey &&
      previousItem.sourceType === 'device' &&
      previousItem.sourceId === previousItem.deviceId &&
      Boolean(quoteContainerEquipmentSourceRevision(previousItem)) &&
      previousItem.itemId === nextItem.itemId &&
      Number.isSafeInteger(previousLeaf.quantity) &&
      previousLeaf.quantity > 0 &&
      Number.isSafeInteger(previousItem.quantity) &&
      previousItem.quantity > 0 &&
      previousItem.quantity % previousLeaf.quantity === 0 &&
      Number.isSafeInteger(previousItem.quantity / previousLeaf.quantity) &&
      previousItem.quantity / previousLeaf.quantity > 0 &&
      previousMemberKey === sourceMemberKey &&
      newSourceChain === undefined;
    if (
      isNewMemberOfExistingLocalPackage ||
      isPreviouslySavedLocalMember ||
      (sourceRevisionId &&
        frozenMembers.length === 1 &&
        frozenMember &&
        (!sourceWasComplete ||
          previousItem.quantity / leaf.quantity === frozenMember.quantity))
    )
      relationQuantity = nextItem.quantity / leaf.quantity;
  }
  if (
    !leaf ||
    fragment.instanceKey !== containerInstanceId ||
    fragment.instanceQuantity !== leaf.quantity ||
    fragment.fragmentKey !== `quote-row:${nextItem.itemId}` ||
    !Number.isSafeInteger(relationQuantity) ||
    (relationQuantity ?? 0) < 1 ||
    nextItem.quantity !== (relationQuantity ?? 0) * leaf.quantity
  ) {
    return false;
  }
  try {
    const expectedMemberKey = quoteContainerSourceMemberKey(nextItem);
    if (!expectedMemberKey || fragment.memberKey !== expectedMemberKey)
      return false;
  } catch {
    return false;
  }
  const immutableSourceFields: Array<keyof QuoteItem> = [
    'deviceId',
    'name',
    'sku',
    'sourceType',
    'sourceId',
    'sourceRevisionId',
    'sourceName',
    'sourceCode',
    'packageRevisionId',
    'planPackageMembershipId',
    'packageRelationId',
    'packageId',
    'replacementPoolId',
    'packageItemPriceSnapshot',
    'planId',
    'formalPlanOccurrence',
    'pricingBaseSnapshot',
  ];
  if (
    immutableSourceFields.some(
      (key) =>
        JSON.stringify(previousItem[key]) !== JSON.stringify(nextItem[key]),
    )
  ) {
    return false;
  }
  const plan = chain?.find((entry) => entry.kind === 'plan');
  const containerRows = nextQuote.items.filter(
    (item) => item.containerInstanceId === containerInstanceId,
  );
  const packageSnapshotsChanged = containerRows.some((item) => {
    const previous = previousQuote.items.find(
      (entry) => entry.itemId === item.itemId,
    );
    return (
      previous?.packageMonthlySubtotalSnapshot !==
        item.packageMonthlySubtotalSnapshot ||
      previous?.packageDiscountedQuoteSnapshot !==
        item.packageDiscountedQuoteSnapshot
    );
  });
  if (packageSnapshotsChanged) {
    const packageGroups = new Map<string, QuoteItem[]>();
    for (const item of containerRows) {
      const key = quoteTableGroupKey(item);
      packageGroups.set(key, [...(packageGroups.get(key) ?? []), item]);
    }
    for (const group of packageGroups.values()) {
      if (
        group.some(
          (item) =>
            item.packageItemPriceSnapshot === undefined ||
            item.packageItemPriceSnapshot === null ||
            !Number.isFinite(item.packageItemPriceSnapshot),
        )
      )
        return false;
      const packageSubtotalCents = group.reduce(
        (sum, item) =>
          sum +
          Math.round(
            (item.packageItemPriceSnapshot as number) * item.quantity * 100,
          ),
        0,
      );
      const discountedSubtotalCents = group.reduce(
        (sum, item) => sum + Math.round(lineTotal(item) * 100),
        0,
      );
      if (
        !Number.isSafeInteger(packageSubtotalCents) ||
        !Number.isSafeInteger(discountedSubtotalCents) ||
        group.some(
          (item) =>
            item.packageMonthlySubtotalSnapshot !==
              packageSubtotalCents / 100 ||
            item.packageDiscountedQuoteSnapshot !==
              discountedSubtotalCents / 100,
        )
      )
        return false;
    }
  }
  if (plan) {
    const frozenPlanPriceFields: Array<keyof QuoteItem> = [
      'planDiscountSnapshot',
      'planProductPriceSnapshot',
      'planProductSubtotalSnapshot',
      'planSubtotalSnapshot',
    ];
    const frozenPlanPricesUnchanged = frozenPlanPriceFields.every(
      (key) =>
        JSON.stringify(previousItem[key]) === JSON.stringify(nextItem[key]),
    );
    if (frozenPlanPricesUnchanged) return true;
    const discount = Number(plan.planDiscountSnapshot);
    const packagePrice = nextItem.packageItemPriceSnapshot;
    const planRelationQuantity = relationQuantity ?? 0;
    if (
      !Number.isFinite(discount) ||
      packagePrice === undefined ||
      packagePrice === null ||
      nextItem.planDiscountSnapshot !== discount ||
      nextItem.planProductPriceSnapshot !==
        planProductPrice(packagePrice, discount) ||
      nextItem.planProductSubtotalSnapshot !==
        planProductSubtotal(packagePrice, discount, planRelationQuantity) ||
      nextItem.planSubtotalSnapshot !==
        planProductSubtotal(packagePrice, discount, planRelationQuantity)
    )
      return false;
  } else if (
    previousItem.planDiscountSnapshot !== nextItem.planDiscountSnapshot ||
    previousItem.planProductPriceSnapshot !==
      nextItem.planProductPriceSnapshot ||
    previousItem.planProductSubtotalSnapshot !==
      nextItem.planProductSubtotalSnapshot ||
    previousItem.planSubtotalSnapshot !== nextItem.planSubtotalSnapshot ||
    previousItem.planPackagePriceSnapshot !==
      nextItem.planPackagePriceSnapshot ||
    previousItem.packageOriginalPriceSnapshot !==
      nextItem.packageOriginalPriceSnapshot
  ) {
    return false;
  }
  return true;
}

function assertInsertedQuoteItemSource(
  state: CpqState,
  item: QuoteItem,
  businessModeCode: BusinessModeCode,
  quoteContext?: Quote,
  previousQuote?: Quote,
): void {
  const same = (left: unknown, right: unknown) =>
    JSON.stringify(left) === JSON.stringify(right);
  const expectedDiscount =
    defaultQuoteDiscountForBusinessMode(businessModeCode);
  if (item.sourceFragment) {
    if (item.sourceFragment.mode === 'quote-customized') {
      const newSourceChain =
        quoteContext && previousQuote && item.containerInstanceId
          ? quoteNewSourceChildChain(
              state,
              previousQuote,
              quoteContext,
              item.containerInstanceId,
            )
          : undefined;
      if (
        !quoteContext ||
        !previousQuote ||
        !isQuoteEditCustomizedRow(
          previousQuote,
          quoteContext,
          item,
          item,
          newSourceChain,
        )
      )
        throw new Error('报价内定制成员必须绑定已存在的套餐或方案容器');
      if (item.sourceType === 'device') {
        // A new current-quote member proves a device tuple, not a catalog
        // package relationship. Its per-container count is quote-local only.
        const device = state.catalog.devices.find(
          (candidate) =>
            candidate.id === item.deviceId && candidate.status === 'active',
        );
        const leaf = quoteContext.containerInstances?.find(
          (instance) => instance.id === item.containerInstanceId,
        );
        const group = quoteContext.quoteGroups?.find(
          (candidate) => candidate.id === item.quoteGroupId,
        );
        const local = item.deviceFieldSnapshot?.quoteLocalPricing;
        const unit = device?.packageItemPrice;
        const exactSnapshot = device && snapshotDeviceMaster(device);
        const { quoteLocalPricing: _local, ...snapshot } =
          item.deviceFieldSnapshot ?? {};
        const parent = leaf?.parentInstanceId
          ? quoteContext.containerInstances?.find(
              (instance) =>
                instance.id === leaf.parentInstanceId &&
                instance.kind === 'plan',
            )
          : undefined;
        const discount =
          local?.exclusiveDiscountPercent === undefined
            ? 100
            : Number(local.exclusiveDiscountPercent);
        const expectedCents =
          unit === undefined || unit === null
            ? Number.NaN
            : parent
              ? leaf &&
                Number.isSafeInteger(item.quantity) &&
                Number.isSafeInteger(leaf.quantity) &&
                leaf.quantity > 0 &&
                item.quantity % leaf.quantity === 0
                ? Math.round(
                    planProductSubtotal(
                      unit,
                      discount,
                      item.quantity / leaf.quantity,
                    ) * 100,
                  ) * leaf.quantity
                : Number.NaN
              : Math.round(unit * item.quantity * 100);
        if (
          !device?.currentRevisionId ||
          !leaf ||
          leaf.kind !== 'package' ||
          group?.containerInstanceId !== leaf.id ||
          item.sourceId !== device.id ||
          item.sourceRevisionId !== device.currentRevisionId ||
          item.sourceName !== device.name ||
          item.name !== device.name ||
          item.sku !== requireDeviceSku(device) ||
          item.stock !== catalogDeviceStock(device) ||
          item.sourceCode !== device.equipmentCode ||
          item.packageId !== undefined ||
          item.packageRevisionId !== undefined ||
          item.packageRelationId !== undefined ||
          (leaf.originKind === 'quote-local-package' &&
            item.packageRelationQuantitySnapshot !== undefined) ||
          item.planId !== undefined ||
          item.localPlanInstanceId !== undefined ||
          item.localPlanMemberId !== undefined ||
          item.planDiscountSnapshot !== undefined ||
          item.planProductPriceSnapshot !== undefined ||
          item.planProductSubtotalSnapshot !== undefined ||
          item.planSubtotalSnapshot !== undefined ||
          item.planPackagePriceSnapshot !== undefined ||
          item.formalPlanOccurrence !== undefined ||
          item.replacementDeviceId !== undefined ||
          item.replacementRelationId !== undefined ||
          unit === undefined ||
          unit === null ||
          !Number.isFinite(unit) ||
          unit < 0 ||
          item.packageItemPriceSnapshot !== unit ||
          item.unitPrice !== unit ||
          !same(snapshot, exactSnapshot) ||
          !same(
            item.pricingBaseSnapshot,
            createPricingBaseSnapshot(businessModeCode, device),
          ) ||
          (local?.exclusiveDiscountPercent === undefined) !==
            (local?.exclusiveLineCents === undefined) ||
          !Number.isFinite(discount) ||
          discount < 0 ||
          (parent?.planDiscountSnapshot != null &&
            discount !== Number(parent.planDiscountSnapshot)) ||
          (local?.exclusiveLineCents !== undefined &&
            local.exclusiveLineCents !== expectedCents) ||
          item.discount !== expectedDiscount
        )
          throw new Error(
            '报价内新增设备必须保留准确目录来源、套餐单品价和当前容器数量',
          );
        return;
      }
      if (newSourceChain && item.sourceType === 'package') {
        const [child, parent] = newSourceChain;
        const pkg = state.catalog.packages.find(
          (candidate) =>
            candidate.id === child.originId &&
            candidate.currentRevisionId === child.originRevisionId,
        );
        const relation = pkg?.productLinks.find(
          (candidate) => candidate.relationId === item.packageRelationId,
        );
        const equipment = relation?.equipment;
        const device =
          relation &&
          equipment &&
          equipment.id === relation.deviceId &&
          equipment.currentRevisionId === relation.deviceRevisionId
            ? (equipment as QuoteSelectionDevice)
            : state.catalog.devices.find(
                (candidate) =>
                  candidate.id === relation?.deviceId &&
                  candidate.currentRevisionId === relation?.deviceRevisionId,
              );
        const { quoteLocalPricing: local, ...snapshot } =
          item.deviceFieldSnapshot ?? {};
        const discount = Number(local?.exclusiveDiscountPercent);
        const parentDiscount =
          parent.planDiscountSnapshot ?? child.planDiscountSnapshot;
        const expectedLine =
          relation?.packageItemPrice == null
            ? Number.NaN
            : Math.round(
                planProductSubtotal(
                  relation.packageItemPrice,
                  discount,
                  relation.quantity,
                ) * 100,
              ) * child.quantity;
        if (
          !pkg ||
          !relation ||
          !device ||
          item.sourceName !== pkg.name ||
          item.sourceCode !== pkg.code ||
          item.replacementPoolId !==
            (relation.replacementPoolId ?? undefined) ||
          item.sourceRevisionId !== pkg.currentRevisionId ||
          (item.packageRevisionId !== undefined &&
            item.packageRevisionId !== pkg.currentRevisionId) ||
          item.name !== device.name ||
          item.sku !== requireDeviceSku(device) ||
          item.stock !== catalogDeviceStock(device) ||
          !same(snapshot, snapshotDeviceMaster(device)) ||
          item.packageItemPriceSnapshot !== relation.packageItemPrice ||
          item.unitPrice !== relation.packageItemPrice ||
          !same(
            item.pricingBaseSnapshot,
            createPricingBaseSnapshot(businessModeCode, {
              packageItemPrice: relation.packageItemPrice,
              taxInclusivePlanPrice: device.taxInclusivePlanPrice,
            }),
          ) ||
          item.planId !== undefined ||
          item.planDiscountSnapshot !== undefined ||
          item.planProductPriceSnapshot !== undefined ||
          item.planProductSubtotalSnapshot !== undefined ||
          item.planSubtotalSnapshot !== undefined ||
          item.planPackagePriceSnapshot !== undefined ||
          item.localPlanInstanceId !== undefined ||
          item.localPlanMemberId !== undefined ||
          item.formalPlanOccurrence !== undefined ||
          item.replacementDeviceId !== undefined ||
          item.replacementRelationId !== undefined ||
          item.discount !== expectedDiscount ||
          !Number.isFinite(discount) ||
          discount < 0 ||
          (parentDiscount != null && discount !== Number(parentDiscount)) ||
          local?.exclusiveLineCents !== expectedLine
        )
          throw new Error(
            '新方案套餐必须保留准确目录成员、设备修订及父方案金额',
          );
        return;
      }
      const {
        sourceFragment: _fragment,
        fragmentAllocation: _allocation,
        ...source
      } = item;
      assertInsertedQuoteItemSource(
        state,
        source,
        businessModeCode,
        quoteContext,
        previousQuote,
      );
      return;
    }
    if (item.sourceFragment.mode !== 'complete')
      throw new Error('回执分行只能由已验证的服务端发布来源生成');
    const allocation = item.fragmentAllocation;
    if (!allocation) throw new Error('分行缺少正式金额');
    const {
      sourceFragment: _fragment,
      fragmentAllocation: _allocation,
      ...whole
    } = item;
    whole.quantity = allocation.wholeQuantity;
    whole.discount = expectedDiscount;
    if (whole.planProductSubtotalSnapshot !== undefined)
      whole.planProductSubtotalSnapshot = allocation.wholeRentalLineCents / 100;
    if (whole.formalPlanOccurrence)
      whole.formalPlanOccurrence = {
        ...whole.formalPlanOccurrence,
        sourceLineCents: allocation.wholeRentalLineCents,
      };
    assertInsertedQuoteItemSource(
      state,
      whole,
      businessModeCode,
      quoteContext,
      previousQuote,
    );
    if (quoteExclusiveLineCents(whole) !== allocation.wholeRentalLineCents)
      throw new Error('分行整关系金额与正式来源不一致');
    return;
  }
  if (item.sourceType === 'temporary') {
    const temporary = state.temporaryDevices.find(
      (candidate) =>
        candidate.id === item.deviceId && candidate.id === item.sourceId,
    );
    if (
      !temporary ||
      item.sourceName !== temporary.name ||
      item.name !== temporary.name ||
      item.sku !== temporary.sku ||
      item.unitPrice !== temporary.price ||
      item.stock !== temporary.stock ||
      item.quantity !== 1 ||
      item.discount !== 100 ||
      !same(
        item.pricingBaseSnapshot,
        createPricingBaseSnapshot('repair', {
          legacyUnitPrice: temporary.price,
        }),
      ) ||
      item.packageRelationId !== undefined ||
      item.packageId !== undefined ||
      item.packageItemPriceSnapshot !== undefined ||
      item.packageRelationQuantitySnapshot !== undefined ||
      item.planId !== undefined ||
      item.planDiscountSnapshot !== undefined ||
      item.planProductPriceSnapshot !== undefined ||
      item.planProductSubtotalSnapshot !== undefined ||
      item.planSubtotalSnapshot !== undefined ||
      item.planPackagePriceSnapshot !== undefined ||
      item.packageMonthlySubtotalSnapshot !== undefined ||
      item.packageOriginalPriceSnapshot !== undefined ||
      item.packageDiscountedQuoteSnapshot !== undefined ||
      item.deviceFieldSnapshot !== undefined
    ) {
      throw new Error('临时报价行必须来自当前可证明的临时设备事实');
    }
    return;
  }

  const currentDevice = state.catalog.devices.find(
    (candidate) => candidate.id === item.deviceId,
  );
  const sourcePlan = item.planId
    ? state.catalog.plans.find((candidate) => candidate.id === item.planId)
    : undefined;
  const sourceFormalPackage = sourcePlan?.formalPackages?.find(
    (candidate) =>
      candidate.packageId === item.packageId &&
      (!item.planPackageMembershipId ||
        candidate.planPackageMembershipId === item.planPackageMembershipId) &&
      (!item.packageRevisionId ||
        candidate.packageRevisionId === item.packageRevisionId),
  );
  if (
    sourcePlan?.formalPackages?.length &&
    !item.formalPlanOccurrence &&
    !item.planPackageMembershipId &&
    sourcePlan.formalPackages?.filter(
      (candidate) =>
        candidate.packageId === item.packageId &&
        (!item.packageRevisionId ||
          candidate.packageRevisionId === item.packageRevisionId),
    ).length !== 1
  )
    throw new Error('方案套餐成员身份不唯一');
  const sourceFormalRelation = sourceFormalPackage?.items.find(
    (candidate) => candidate.packageRelationId === item.packageRelationId,
  );
  const sourcePackageRelation = state.catalog.packages
    .find((candidate) => candidate.id === item.packageId)
    ?.productLinks.find(
      (candidate) => candidate.relationId === item.packageRelationId,
    );
  const sourcePackageEquipment = sourcePackageRelation?.equipment;
  const sourcePackageDevice =
    sourcePackageRelation &&
    sourcePackageEquipment?.id === sourcePackageRelation.deviceId &&
    sourcePackageEquipment.currentRevisionId ===
      sourcePackageRelation.deviceRevisionId
      ? (sourcePackageEquipment as QuoteSelectionDevice)
      : undefined;
  const sourceOccurrence = sourcePlan?.occurrenceModel?.occurrences.find(
    (candidate) =>
      candidate.occurrenceKey === item.formalPlanOccurrence?.occurrenceKey,
  );
  const sourceOccurrenceItem = sourceOccurrence?.items.find(
    (candidate) => candidate.itemKey === item.formalPlanOccurrence?.itemKey,
  );
  // 旧模式方案冻结 exact 套餐与设备 revision。当前设备 head 后续变化时，
  // 报价行必须继续校验方案内的历史设备快照，不能回退到当前目录重算。
  const device =
    sourceOccurrenceItem?.deviceSnapshot ??
    sourceFormalRelation?.deviceSnapshot ??
    sourcePackageDevice ??
    currentDevice;
  if (item.localPlanInstanceId || item.localPlanMemberId) {
    const quote = quoteContext;
    const instance = quote?.localPlanInstances?.find(
      (candidate) =>
        candidate.id === item.localPlanInstanceId &&
        candidate.status === 'active' &&
        candidate.businessModeCode === businessModeCode,
    );
    const member = instance?.members.find(
      (candidate) => candidate.planMemberId === item.localPlanMemberId,
    );
    const group = quote?.quoteGroups?.find(
      (candidate) => candidate.id === item.quoteGroupId,
    );
    const deviceRevisionId = device?.currentRevisionId;
    const equipmentMember = deviceRevisionId
      ? member?.equipmentMembers.find(
          (candidate) =>
            candidate.deviceId === item.deviceId &&
            candidate.deviceRevisionId === deviceRevisionId &&
            candidate.packageRelationId === (item.packageRelationId ?? null) &&
            candidate.packageItemPriceCents ===
              (item.packageItemPriceSnapshot === undefined
                ? null
                : Math.round(item.packageItemPriceSnapshot * 100)) &&
            candidate.businessModeCode === businessModeCode,
        )
      : undefined;
    const localPricing = item.deviceFieldSnapshot?.quoteLocalPricing;
    const memberSource = member?.sourceSnapshot;
    const sourceMatchesInstance =
      memberSource?.type === 'catalog-package'
        ? item.sourceType === 'package' &&
          item.sourceId === memberSource.packageId &&
          item.sourceRevisionId === memberSource.packageRevisionId &&
          item.packageId === memberSource.packageId
        : memberSource?.type === 'local-package'
          ? item.sourceType === 'device' &&
            item.sourceId === item.deviceId &&
            item.sourceRevisionId === deviceRevisionId &&
            item.packageId === undefined
          : false;
    const packageQuantity =
      (instance?.quantity ?? 0) * (member?.quantityPerSet ?? 0);
    const effectivePlanDiscount = instance
      ? quoteItemEffectiveDiscount(item, instance.discountPercentSnapshot)
      : undefined;
    const expectedLineCents =
      item.packageItemPriceSnapshot === undefined ||
      item.planDiscountSnapshot === undefined ||
      !equipmentMember
        ? Number.NaN
        : Math.round(
            planProductSubtotal(
              item.packageItemPriceSnapshot,
              item.planDiscountSnapshot,
              equipmentMember.quantityPerPackage,
            ) * 100,
          ) * packageQuantity;
    const localPlanChecks = [
      ['device', Boolean(device)],
      ['quote', Boolean(quote)],
      ['instance', Boolean(instance)],
      ['member', Boolean(member)],
      ['equipmentMember', Boolean(equipmentMember)],
      ['sourceSnapshot', sourceMatchesInstance],
      ['group', Boolean(group)],
      ['groupInstance', group?.planInstanceId === instance?.id],
      ['groupMember', group?.planMemberId === member?.planMemberId],
      // A group's numeric id is persisted as quoteGroupId. Its parent
      // relationship is established by planInstanceId + planMemberId above.
      ['groupId', Boolean(group) && group?.id === item.quoteGroupId],
      ['markerInstance', item.localPlanInstanceId === instance?.id],
      ['markerMember', item.localPlanMemberId === member?.planMemberId],
      ['name', item.name === device?.name],
      ['sku', item.sku === device?.sku],
      [
        'stock',
        item.stock === (device ? catalogDeviceStock(device) : undefined),
      ],
      [
        'deviceSnapshot',
        same(
          item.deviceFieldSnapshot
            ? (({ quoteLocalPricing: _pricing, ...snapshot }) => snapshot)(
                item.deviceFieldSnapshot,
              )
            : undefined,
          device ? deviceFieldSnapshotForQuoteItem(device) : undefined,
        ),
      ],
      [
        'discountSnapshot',
        instance !== undefined &&
          item.planDiscountSnapshot === effectivePlanDiscount,
      ],
      ['quoteDiscount', item.discount === expectedDiscount],
      ['unitPrice', item.unitPrice === item.packageItemPriceSnapshot],
      [
        'quantity',
        item.quantity ===
          (instance?.quantity ?? 0) *
            (member?.quantityPerSet ?? 0) *
            (equipmentMember?.quantityPerPackage ?? 0),
      ],
      [
        'pricingBase',
        same(
          item.pricingBaseSnapshot,
          device && item.packageItemPriceSnapshot !== undefined
            ? createPricingBaseSnapshot(businessModeCode, {
                packageItemPrice: item.packageItemPriceSnapshot,
                taxInclusivePlanPrice: device.taxInclusivePlanPrice,
              })
            : undefined,
        ),
      ],
      ['noCatalogPlanId', item.planId === undefined],
      ['lineCents', Number.isSafeInteger(expectedLineCents)],
      ['localPricing', Boolean(localPricing)],
      [
        'exclusiveDiscount',
        localPricing?.exclusiveDiscountPercent ===
          String(effectivePlanDiscount),
      ],
      ['exclusiveLine', localPricing?.exclusiveLineCents === expectedLineCents],
      [
        'basisLine',
        localPricing?.exclusiveLineBasisCents === expectedLineCents,
      ],
      [
        'basisQuantity',
        localPricing?.exclusiveLineBasisQuantity === packageQuantity,
      ],
      [
        'subtotal',
        item.planProductSubtotalSnapshot === expectedLineCents / 100,
      ],
      [
        'unitPlanPrice',
        item.planProductPriceSnapshot ===
          (item.packageItemPriceSnapshot === undefined || !instance
            ? undefined
            : planProductPrice(
                item.packageItemPriceSnapshot,
                effectivePlanDiscount ?? instance.discountPercentSnapshot,
              )),
      ],
    ] as const;
    if (localPlanChecks.some(([, passes]) => !passes)) {
      throw new Error('报价内方案行必须绑定有效方案实例、成员和已验证金额');
    }
    return;
  }
  if (
    !device ||
    item.name !== device.name ||
    item.sku !== device.sku ||
    item.stock !== catalogDeviceStock(device) ||
    !same(item.deviceFieldSnapshot, deviceFieldSnapshotForQuoteItem(device))
  ) {
    throw new Error('报价行设备快照与当前目录事实不一致');
  }
  // 独立实例以方案不可变版本为真源，不能再要求存在于全局套餐关系表。
  if (item.formalPlanOccurrence) {
    const tuple = item.formalPlanOccurrence;
    const plan = state.catalog.plans.find(
      (candidate) => candidate.id === item.planId,
    );
    const occurrence = plan?.occurrenceModel?.occurrences.find(
      (candidate) => candidate.occurrenceKey === tuple.occurrenceKey,
    );
    const line = occurrence?.items.find(
      (candidate) => candidate.itemKey === tuple.itemKey,
    );
    const expectedView = occurrencePricingView(businessModeCode);
    const purchaseUnit = purchasePriceSnapshotCents(
      device,
      tuple.purchasePriceBasis,
    );
    const occurrencePlanProductPrice =
      line?.monthlyPriceCents == null
        ? undefined
        : occurrenceSourceLineCents({
            formalPlanOccurrence: tuple,
            packageItemPriceSnapshot: line.monthlyPriceCents / 100,
            quantity: 1,
          }) / 100;
    if (
      plan?.status !== 'active' ||
      plan.occurrenceModel?.targetStatus !== 'active' ||
      plan.incompleteReasons?.length ||
      !occurrence ||
      !line ||
      line.quantity === null ||
      item.sourceType !== 'plan' ||
      item.sourceId !== plan.id ||
      item.sourceName !== plan.name ||
      item.sourceRevisionId !== plan.currentRevisionId ||
      item.packageId !== occurrence?.packageId ||
      tuple.packageRevisionId !== occurrence.packageRevisionId ||
      tuple.deviceRevisionId !== device.currentRevisionId ||
      line.deviceRevisionId !== tuple.deviceRevisionId ||
      line.deviceId !== device.id ||
      !Number.isSafeInteger(item.quantity) ||
      item.quantity < line.quantity ||
      item.quantity % line.quantity !== 0 ||
      item.packageRelationQuantitySnapshot !== line.quantity ||
      line.monthlyPriceCents === null ||
      item.packageItemPriceSnapshot !== line.monthlyPriceCents / 100 ||
      !same(
        item.pricingBaseSnapshot,
        createPricingBaseSnapshot(businessModeCode, {
          packageItemPrice: line.monthlyPriceCents / 100,
          taxInclusivePlanPrice: device.taxInclusivePlanPrice,
        }),
      ) ||
      item.planDiscountSnapshot !== undefined ||
      item.planProductPriceSnapshot !== occurrencePlanProductPrice ||
      item.planProductSubtotalSnapshot !==
        (occurrencePlanProductPrice ?? Number.NaN) * item.quantity ||
      tuple.pricingCoefficient !== plan.occurrenceModel.pricingCoefficient ||
      tuple.rounding !== plan.occurrenceModel.rounding ||
      tuple.pricingView !== expectedView ||
      (expectedView === 'purchase' &&
        (purchaseUnit === null ||
          tuple.purchaseUnitPriceCents !== purchaseUnit)) ||
      item.discount !== expectedDiscount ||
      tuple.sourceLineCents !== occurrenceSourceLineCents(item)
    ) {
      throw new Error(
        `方案实例来源快照不一致：${tuple.occurrenceKey}/${tuple.itemKey}`,
      );
    }
    return;
  }
  const relationFields = [
    item.packageRelationId,
    item.packageId,
    item.packageItemPriceSnapshot,
    item.packageRelationQuantitySnapshot,
  ];
  // A quote-local package keeps its own group definition. Its rows retain an
  // equipment source and the exact equipment package-item price, without a
  // catalog package relation to validate against.
  if (
    item.quoteGroupId &&
    item.sourceType === 'device' &&
    item.packageRelationId === undefined &&
    item.packageId === undefined &&
    item.packageRelationQuantitySnapshot === undefined &&
    item.packageItemPriceSnapshot !== undefined
  ) {
    const packageItemPrice = device.packageItemPrice;
    if (
      item.sourceId !== device.id ||
      item.sourceName !== device.name ||
      item.sourceRevisionId !== device.currentRevisionId ||
      packageItemPrice === undefined ||
      packageItemPrice === null ||
      !Number.isFinite(packageItemPrice) ||
      packageItemPrice < 0 ||
      item.packageItemPriceSnapshot !== packageItemPrice ||
      item.unitPrice !== packageItemPrice ||
      !same(
        item.pricingBaseSnapshot,
        createPricingBaseSnapshot(businessModeCode, {
          rentalPrice: device.rentalPrice,
          retailPriceTax13: device.retailPriceTax13,
          taxInclusivePlanPrice: device.taxInclusivePlanPrice,
          packageItemPrice,
        }),
      ) ||
      !Number.isSafeInteger(item.quantity) ||
      item.quantity < 1 ||
      item.discount !== expectedDiscount ||
      item.planId !== undefined ||
      item.planDiscountSnapshot !== undefined
    ) {
      throw new Error('报价内套餐设备必须来自当前设备的套餐单品价');
    }
    return;
  }
  const hasRelation = relationFields.some((value) => value !== undefined);
  if (!hasRelation) {
    if (
      item.sourceType !== 'device' ||
      item.sourceId !== device.id ||
      item.sourceName !== device.name ||
      item.unitPrice !==
        (device.rentalPrice ??
          createPricingBaseSnapshot(businessModeCode, device).amount) ||
      !same(
        item.pricingBaseSnapshot,
        createPricingBaseSnapshot(businessModeCode, {
          rentalPrice: device.rentalPrice,
          retailPriceTax13: device.retailPriceTax13,
          taxInclusivePlanPrice: device.taxInclusivePlanPrice,
          legacyUnitPrice: item.unitPrice,
        }),
      ) ||
      item.quantity !== 1 ||
      item.discount !== expectedDiscount ||
      item.planId !== undefined ||
      item.planDiscountSnapshot !== undefined
    ) {
      throw new Error('直接设备报价行必须来自当前设备目录事实');
    }
    return;
  }
  if (relationFields.some((value) => value === undefined)) {
    throw new Error('套餐关系快照不完整');
  }
  const pkg = state.catalog.packages.find(
    (candidate) => candidate.id === item.packageId,
  );
  const relation = pkg?.productLinks.find(
    (candidate) => candidate.relationId === item.packageRelationId,
  );
  const plan = sourcePlan;
  const formalPackage = sourceFormalPackage;
  const formalRelation = sourceFormalRelation;
  const effectiveRelation = formalRelation
    ? {
        relationId: formalRelation.packageRelationId,
        packageId: formalRelation.packageId,
        deviceId: formalRelation.deviceId,
        packageItemPrice:
          formalRelation.packageItemMonthlyPriceCents == null
            ? Number.NaN
            : formalRelation.packageItemMonthlyPriceCents / 100,
        quantity: formalRelation.quantity,
      }
    : relation;
  const effectivePackageSubtotal = formalPackage
    ? formalPackage.packageMonthlySubtotalCents == null
      ? Number.NaN
      : formalPackage.packageMonthlySubtotalCents / 100
    : pkg
      ? pkg.packageMonthlyTotalCents == null
        ? packageMonthlySubtotal(pkg.productLinks)
        : pkg.packageMonthlyTotalCents / 100
      : Number.NaN;
  const packageQuantity = item.quantity / (effectiveRelation?.quantity ?? 0);
  if (
    (!formalPackage && !pkg) ||
    !effectiveRelation ||
    effectiveRelation.packageId !== (formalPackage?.packageId ?? pkg?.id) ||
    effectiveRelation.deviceId !== item.deviceId ||
    effectiveRelation.packageItemPrice !== item.packageItemPriceSnapshot ||
    effectiveRelation.quantity !== item.packageRelationQuantitySnapshot ||
    !same(
      item.pricingBaseSnapshot,
      createPricingBaseSnapshot(businessModeCode, {
        packageItemPrice: effectiveRelation.packageItemPrice,
        taxInclusivePlanPrice: device.taxInclusivePlanPrice,
      }),
    ) ||
    effectivePackageSubtotal !==
      (item.packageMonthlySubtotalSnapshot ??
        item.packageOriginalPriceSnapshot) ||
    item.unitPrice !== effectiveRelation.packageItemPrice ||
    !Number.isSafeInteger(packageQuantity) ||
    packageQuantity < 1 ||
    item.discount !== expectedDiscount
  ) {
    throw new Error('报价行套餐关系快照与当前目录事实不一致');
  }
  if (item.planId === undefined) {
    if (
      !pkg ||
      item.sourceType !== 'package' ||
      item.sourceId !== pkg.id ||
      item.sourceName !== pkg.name ||
      item.planDiscountSnapshot !== undefined ||
      item.planProductPriceSnapshot !== undefined ||
      item.planProductSubtotalSnapshot !== undefined ||
      item.planSubtotalSnapshot !== undefined ||
      item.planPackagePriceSnapshot !== undefined ||
      item.packageDiscountedQuoteSnapshot !== effectivePackageSubtotal
    ) {
      throw new Error('套餐报价行来源绑定无效');
    }
    return;
  }
  const expectedPlanUnitPrice = formalRelation
    ? formalRelation.planUnitMonthlyPriceCents == null
      ? Number.NaN
      : formalRelation.planUnitMonthlyPriceCents / 100
    : plan
      ? planProductPrice(effectiveRelation.packageItemPrice, plan.discount)
      : NaN;
  const expectedPlanLineSubtotal = formalRelation
    ? formalRelation.lineMonthlySubtotalCents == null
      ? Number.NaN
      : formalRelation.lineMonthlySubtotalCents / 100
    : plan
      ? planProductSubtotal(
          effectiveRelation.packageItemPrice,
          plan.discount,
          effectiveRelation.quantity,
        )
      : NaN;
  const expectedPlanSubtotal = formalPackage
    ? formalPackage.items.reduce(
        (sum, candidate) =>
          sum +
          (candidate.lineMonthlySubtotalCents == null
            ? Number.NaN
            : candidate.lineMonthlySubtotalCents / 100),
        0,
      )
    : plan && pkg
      ? planSubtotal(pkg.productLinks, plan.discount)
      : NaN;
  if (formalPackage && plan?.formalDiscount === undefined)
    throw new Error('方案原始计价信息缺失，请刷新后重试');
  if (
    !plan ||
    (!formalPackage && !pkg?.planIds.includes(plan.id)) ||
    item.sourceType !== 'plan' ||
    item.sourceId !== plan.id ||
    item.sourceName !== plan.name ||
    item.planDiscountSnapshot !==
      (plan.formalDiscount === null
        ? undefined
        : formalPackage
          ? plan.formalDiscount
          : plan.discount) ||
    item.planProductPriceSnapshot !== expectedPlanUnitPrice ||
    (item.planProductSubtotalSnapshot !== undefined &&
      item.planProductSubtotalSnapshot !==
        expectedPlanLineSubtotal * packageQuantity) ||
    item.planSubtotalSnapshot !== expectedPlanSubtotal ||
    item.packageDiscountedQuoteSnapshot !== expectedPlanSubtotal
  ) {
    throw new Error('方案报价行来源或折扣快照绑定无效');
  }
}

function nextAudit(
  state: CpqState,
  action: string,
  result: AuditRecord['result'],
  detail: string,
  quote?: Quote,
): AuditRecord {
  return {
    id: `AUD-${String(state.nextSequence).padStart(4, '0')}`,
    quoteId: quote?.quoteId,
    quoteVersion: quote?.version,
    action,
    result,
    detail,
    createdAt: DEMO_TIME,
  };
}

function appendAudit(
  state: CpqState,
  action: string,
  result: AuditRecord['result'],
  detail: string,
  quote?: Quote,
): CpqState {
  state.audits.unshift(nextAudit(state, action, result, detail, quote));
  state.nextSequence += 1;
  return state;
}

function requireQuote(state: CpqState, quoteId: string): Quote {
  const quote = state.quotes[quoteId];
  if (!quote) throw new Error(`报价 ${quoteId} 不存在`);
  return quote;
}

function createBlankQuote(
  quoteId: string,
  version = 1,
  selectedFieldKeys = DEFAULT_QUOTE_FIELD_KEYS,
  businessModes = PRODUCT_CATALOG_SEED.businessModes,
): Quote {
  const defaultDefinitions = PRODUCT_CATALOG_SEED.quoteFieldDefinitions;
  const defaultMode =
    businessModes.find(
      (mode) => mode.code === DEFAULT_QUOTE_BUSINESS_MODE && mode.active,
    ) ?? activeBusinessModes(businessModes)[0];
  if (!defaultMode) throw new Error('至少需要一个启用的业务模式才能创建新草稿');
  return {
    quoteId,
    masterId: quoteId,
    version,
    title: version > 1 ? `新报价单 V${version}` : '新报价单',
    customer: { name: '', customerNo: '', contact: '', phone: '' },
    validUntil: '',
    businessModeCode: defaultMode.code,
    businessModeLabelSnapshot: defaultMode.label,
    businessModeRequiresReselection: false,
    outputTermsRequired: false,
    deposit: 0,
    overallDiscount: defaultQuoteDiscountForBusinessMode(defaultMode.code),
    items: [],
    customColumns: selectedFieldKeys.filter(
      (fieldKey) => seedFieldSelection(fieldKey)?.placement === 'quote-line',
    ),
    fieldSelections: selectedFieldKeys.flatMap((fieldKey) => {
      const selection = seedFieldSelection(fieldKey);
      return selection ? [selection] : [];
    }),
    customColumnSnapshot: defaultDefinitions
      .filter((field) => selectedFieldKeys.includes(field.key))
      .sort(
        (left, right) =>
          selectedFieldKeys.indexOf(left.key) -
          selectedFieldKeys.indexOf(right.key),
      )
      .map((field) => ({
        key: field.key,
        label: field.label,
        type: field.type,
        width: field.width,
        note: field.note,
        options: field.options?.map((option) => ({ ...option })),
        sourceKind: field.sourceKind,
        fieldKey: field.governance.fieldKey,
        definitionVersion: field.governance.definitionVersion,
        formulaVersion: field.governance.formulaVersion,
        ...(field.calculation
          ? { calculation: structuredClone(field.calculation) }
          : {}),
        placement: seedFieldSelection(field.key)?.placement ?? 'quote-line',
      })),
    status: 'draft',
    dirty: false,
    createdAt: DEMO_TIME,
    updatedAt: DEMO_TIME,
  };
}

interface DemoQuoteConfig {
  quoteId: string;
  title: string;
  customerIndex: number;
  status: QuoteStatus;
  businessModeCode: Quote['businessModeCode'];
  deviceIds: string[];
  quantities?: number[];
  discounts?: number[];
  version?: number;
  sourceQuoteId?: string;
  sourceVersion?: number;
  sourceReceiptId?: string;
  dirty?: boolean;
  activeOperationId?: string;
  failureReason?: string;
  salesOrderId?: string;
}

function createDemoItems(config: DemoQuoteConfig): QuoteItem[] {
  return config.deviceIds.map((deviceId, index) => {
    const device = PRODUCT_CATALOG_SEED.devices.find(
      (record) => record.id === deviceId,
    );
    if (!device) throw new Error(`演示设备 ${deviceId} 不存在`);
    return {
      itemId: `${config.quoteId}-ITEM-${String(index + 1).padStart(2, '0')}`,
      deviceId: device.id,
      name: device.name,
      sku: requireDeviceSku(device),
      sourceType:
        index === 0
          ? 'plan'
          : index === config.deviceIds.length - 1
            ? 'device'
            : 'package',
      sourceId:
        index === 0
          ? 'PLAN-DEMO'
          : index === config.deviceIds.length - 1
            ? device.id
            : 'PKG-DEMO',
      sourceName:
        index === 0
          ? '演示主方案'
          : index === config.deviceIds.length - 1
            ? device.name
            : '演示套餐',
      quantity: config.quantities?.[index] ?? 1,
      unitPrice:
        device.rentalPrice ??
        createPricingBaseSnapshot(config.businessModeCode, device).amount,
      discount: config.discounts?.[index] ?? 100,
      pricingBaseSnapshot: createPricingBaseSnapshot(config.businessModeCode, {
        rentalPrice: device.rentalPrice,
        retailPriceTax13: device.retailPriceTax13,
        taxInclusivePlanPrice: device.taxInclusivePlanPrice,
        legacyUnitPrice: device.rentalPrice,
      }),
      deviceFieldSnapshot: deviceFieldSnapshotForQuoteItem(device),
      stock: Object.values(device.stockByWarehouse).reduce(
        (total, quantity) => total + quantity,
        0,
      ),
      replacementDeviceId: Object.values(device.stockByWarehouse).every(
        (quantity) => quantity === 0,
      )
        ? device.category === '网络'
          ? 'DEV-024'
          : 'DEV-016'
        : undefined,
      remark: Object.values(device.stockByWarehouse).every(
        (quantity) => quantity === 0,
      )
        ? '零库存场景：需平替、提醒或重新同步'
        : index === 1
          ? '演示备注：客户关注交付时间'
          : undefined,
    };
  });
}

function createScenarioQuote(config: DemoQuoteConfig): Quote {
  const customer = PRODUCT_CATALOG_SEED.customers[config.customerIndex];
  if (!customer) throw new Error(`演示客户索引 ${config.customerIndex} 不存在`);
  return {
    quoteId: config.quoteId,
    masterId: config.quoteId,
    version: config.version ?? 1,
    sourceQuoteId: config.sourceQuoteId,
    sourceVersion: config.sourceVersion,
    sourceReceiptId: config.sourceReceiptId,
    title: config.title,
    customer: {
      customerId: customer.id,
      managerUserId: customer.managerUserId,
      name: customer.name,
      customerNo: customer.customerNo,
      contact: customer.contact,
      phone: customer.phone,
    },
    validUntil:
      config.status === 'void' || config.status === 'archived'
        ? '2026-07-31'
        : '2026-09-30',
    businessModeCode: config.businessModeCode,
    businessModeLabelSnapshot: businessModeLabel(
      PRODUCT_CATALOG_SEED.businessModes,
      config.businessModeCode,
    ),
    businessModeRequiresReselection:
      config.status === 'draft' &&
      !isStableBusinessModeCode(config.businessModeCode),
    deposit: config.businessModeCode === 'rental' ? 5000 : 0,
    overallDiscount: config.status === 'success' ? 95 : 100,
    items: createDemoItems(config),
    customColumns:
      config.status === 'draft'
        ? DEFAULT_QUOTE_FIELD_KEYS.filter(
            (fieldKey) =>
              seedFieldSelection(fieldKey)?.placement === 'quote-line',
          )
        : config.businessModeCode === 'rental'
          ? ['租价', '库存', '备注']
          : ['产品属性', 'SKU', '库存', '备注'],
    fieldSelections:
      config.status === 'draft'
        ? DEFAULT_QUOTE_FIELD_KEYS.flatMap((fieldKey) => {
            const selection = seedFieldSelection(fieldKey);
            return selection ? [selection] : [];
          })
        : undefined,
    customColumnSnapshot:
      config.status === 'draft'
        ? PRODUCT_CATALOG_SEED.quoteFieldDefinitions
            .filter((field) => DEFAULT_QUOTE_FIELD_KEYS.includes(field.key))
            .sort(
              (left, right) =>
                DEFAULT_QUOTE_FIELD_KEYS.indexOf(left.key) -
                DEFAULT_QUOTE_FIELD_KEYS.indexOf(right.key),
            )
            .map((field) => ({
              key: field.key,
              label: field.label,
              type: field.type,
              width: field.width,
              note: field.note,
              options: field.options?.map((option) => ({ ...option })),
              sourceKind: field.sourceKind,
              fieldKey: field.governance.fieldKey,
              definitionVersion: field.governance.definitionVersion,
              formulaVersion: field.governance.formulaVersion,
              ...(field.calculation
                ? { calculation: structuredClone(field.calculation) }
                : {}),
              placement:
                seedFieldSelection(field.key)?.placement ?? 'quote-line',
            }))
        : undefined,
    status: config.status,
    dirty: config.dirty ?? config.status === 'draft',
    createdAt: DEMO_TIME,
    updatedAt: DEMO_TIME,
    activeOperationId: config.activeOperationId,
    failureReason: config.failureReason,
    salesOrderId: config.salesOrderId,
  };
}

function fixtureReceiptSnapshot(
  items: QuoteItem[],
  overallDiscount: number,
): ReceiptSelectionSnapshot {
  const effectiveItems = structuredClone(items);
  const amounts = quoteAmounts({ items: effectiveItems, overallDiscount });
  return {
    kind: 'structured',
    items: effectiveItems.map((item) => ({
      selectionKind: item.sourceType === 'receipt' ? 'added' : 'kept',
      effectiveItem: item,
      originalItemSnapshot:
        item.sourceType === 'receipt' ? null : structuredClone(item),
    })),
    overallDiscountSnapshot: overallDiscount,
    subtotalSnapshot: amounts.subtotal,
    totalSnapshot: amounts.total,
    submittedAt: DEMO_TIME,
  };
}

function fixtureReceiptSnapshotWithDemoChange(
  receiptId: string,
  items: QuoteItem[],
  overallDiscount: number,
): ReceiptSelectionSnapshot {
  const snapshot = fixtureReceiptSnapshot(items, overallDiscount);
  if (snapshot.kind !== 'structured') return snapshot;
  if (receiptId === 'RECEIPT-0004' || receiptId === 'RECEIPT-0005') {
    snapshot.items = items.map((item) => ({
      selectionKind: 'kept',
      effectiveItem: structuredClone(item),
      originalItemSnapshot: structuredClone(item),
    }));
  }
  const addedItems = (deviceIds: string[]): ReceiptSelectionItemSnapshot[] =>
    createDemoItems({
      quoteId: `${receiptId}-ADDED`,
      title: '客户反馈新增设备',
      customerIndex: 0,
      status: 'draft',
      businessModeCode: 'rental',
      deviceIds,
    }).map((item) => ({
      selectionKind: 'added',
      effectiveItem: {
        ...item,
        sourceType: 'receipt',
        sourceId: receiptId,
        sourceName: '客户反馈',
      },
      originalItemSnapshot: null,
    }));
  const removeItem = (index: number) => {
    const target = snapshot.items[index];
    if (!target?.effectiveItem) return;
    target.selectionKind = 'removed';
    target.effectiveItem = null;
  };

  switch (receiptId) {
    case 'RECEIPT-0003':
      if (snapshot.items[1]?.effectiveItem)
        snapshot.items[1].effectiveItem.quantity += 1;
      break;
    case 'RECEIPT-0004':
      removeItem(0);
      snapshot.items.push(...addedItems(['DEV-002']));
      break;
    case 'RECEIPT-0005':
      if (snapshot.items[1]?.effectiveItem)
        snapshot.items[1].effectiveItem.quantity += 1;
      break;
    case 'RECEIPT-0006':
      if (snapshot.items[0]?.effectiveItem)
        snapshot.items[0].effectiveItem.quantity += 2;
      break;
    case 'RECEIPT-0007':
      removeItem(1);
      break;
    case 'RECEIPT-0008':
      snapshot.items.push(...addedItems(['DEV-005']));
      break;
    case 'RECEIPT-0009':
      if (snapshot.items[1]?.effectiveItem)
        snapshot.items[1].effectiveItem.quantity += 1;
      break;
    case 'RECEIPT-0010': {
      const target = snapshot.items[0];
      const [replacement] = addedItems(['DEV-002']);
      if (
        target?.effectiveItem &&
        target.originalItemSnapshot &&
        replacement?.effectiveItem
      ) {
        const original = target.originalItemSnapshot;
        target.selectionKind = 'replaced';
        target.effectiveItem = {
          ...replacement.effectiveItem,
          itemId: original.itemId,
          quantity: original.quantity,
        };
        target.replacementSource = {
          requestedDeviceId: replacement.effectiveItem.deviceId,
          originalDeviceId: original.deviceId,
          originalItemId: original.itemId,
        };
      }
      break;
    }
    case 'RECEIPT-0011':
      snapshot.items.push(...addedItems(['DEV-001', 'DEV-006']));
      break;
    case 'RECEIPT-0012':
      snapshot.items.forEach((_, index) => {
        removeItem(index);
      });
      snapshot.items.push(
        ...addedItems(
          Array.from(
            { length: 18 },
            (_, index) => `DEV-${String(index + 1).padStart(3, '0')}`,
          ),
        ),
      );
      break;
    case 'RECEIPT-0016':
      if (snapshot.items[0]?.effectiveItem)
        snapshot.items[0].effectiveItem.quantity += 2;
      break;
    default:
      break;
  }
  const effectiveItems = snapshot.items.flatMap((item) =>
    item.effectiveItem ? [item.effectiveItem] : [],
  );
  const amounts = quoteAmounts({ items: effectiveItems, overallDiscount });
  return {
    ...snapshot,
    subtotalSnapshot: amounts.subtotal,
    totalSnapshot: amounts.total,
  };
}

const additionalFeedbackFixtures = [
  {
    sequence: 6,
    quoteId: 'Q-V2-0005',
    customer: '户外活动项目组',
    phone: '13800000005',
    note: '主机数量由 1 台增加为 3 台，请同步更新配置。',
    createdAt: '2026-08-14T08:20:00.000Z',
  },
  {
    sequence: 7,
    quoteId: 'Q-V2-0010',
    customer: '短视频项目组',
    phone: '13800000010',
    note: '删除暂时不需要的网络设备，其他设备保持不变。',
    createdAt: '2026-08-15T10:05:00.000Z',
  },
  {
    sequence: 8,
    quoteId: 'Q-V2-0011',
    customer: '直播项目采购组',
    phone: '13800000011',
    note: '新增一台 Sony FX3，档期已确认。',
    createdAt: '2026-08-16T11:40:00.000Z',
  },
  {
    sequence: 9,
    quoteId: 'Q-V2-0012',
    customer: '访谈录制项目组',
    phone: '13800000012',
    note: '第二项设备增加 1 台，请按最新数量更新。',
    createdAt: '2026-08-17T13:15:00.000Z',
  },
  {
    sequence: 10,
    quoteId: 'Q-V2-0013',
    customer: '品牌活动采购组',
    phone: '13800000013',
    note: '第一项设备替换为 Sony A7M4，请重新生成版本。',
    createdAt: '2026-08-18T15:30:00.000Z',
  },
  {
    sequence: 11,
    quoteId: 'Q-V2-0014',
    customer: '课程直播项目组',
    phone: '13800000014',
    note: '新增两项设备，原配置全部保留。',
    createdAt: '2026-08-19T09:45:00.000Z',
  },
  {
    sequence: 12,
    quoteId: 'Q-V2-0015',
    customer: '大型活动项目组',
    phone: '13800000015',
    note: '本次共调整 20 项设备：删除原 2 项，新增 18 项，请完整更新。',
    createdAt: '2026-08-20T16:10:00.000Z',
  },
  {
    sequence: 16,
    quoteId: 'Q-V2-0017',
    customer: '商业直播采购组',
    phone: '13800000017',
    note: '第一项设备增加 2 台，请销售更新新的分享版本。',
    createdAt: '2026-08-21T10:20:00.000Z',
  },
] as const;

const MULTI_ROUND_FEEDBACK_MASTER_ID = 'Q-V2-0002';
const MULTI_ROUND_FEEDBACK_V2_ID = 'Q-V2-MOCK-0002-V2';
const MULTI_ROUND_FEEDBACK_V3_ID = 'Q-V2-MOCK-0002-V3';
const MULTI_ROUND_FEEDBACK_V2_PUBLICATION_ID = 'PUB-1101';
const MULTI_ROUND_FEEDBACK_V3_PUBLICATION_ID = 'PUB-1102';

function createDemoFeedbackVersion(
  state: CpqState,
  input: {
    sourceQuoteId: string;
    quoteId: string;
    version: number;
    sourceReceiptId: string;
    createdAt: string;
  },
): Quote {
  const source = state.quotes[input.sourceQuoteId];
  if (!source)
    throw new Error(`演示反馈来源报价 ${input.sourceQuoteId} 不存在`);
  const selection = fixtureReceiptSnapshotWithDemoChange(
    input.sourceReceiptId,
    source.items,
    source.overallDiscount,
  );
  if (selection.kind !== 'structured') {
    throw new Error(`演示反馈 ${input.sourceReceiptId} 缺少结构化设备记录`);
  }
  const items = selection.items.flatMap((item) =>
    item.effectiveItem ? [item.effectiveItem] : [],
  );
  const quote: Quote = {
    ...structuredClone(source),
    quoteId: input.quoteId,
    masterId: MULTI_ROUND_FEEDBACK_MASTER_ID,
    version: input.version,
    sourceQuoteId: source.quoteId,
    sourceVersion: source.version,
    sourceReceiptId: input.sourceReceiptId,
    title: `${source.customer.name} V${input.version}`,
    status: 'collaboration',
    dirty: false,
    items: items.map((item) => {
      const { calculationSnapshots: _staleSnapshots, ...stableItem } = item;
      return stableItem;
    }),
    createdAt: input.createdAt,
    updatedAt: input.createdAt,
  };
  return bindLatestSelectedCalculations(state, quote);
}

function buildDemoState(): CpqState {
  const scenarioQuotes = [
    createScenarioQuote({
      quoteId: 'Q-V2-0001',
      title: '新品发布会草稿（库存不足）',
      customerIndex: 0,
      status: 'draft',
      businessModeCode: 'trade',
      deviceIds: ['DEV-013', 'DEV-001', 'DEV-012', 'DEV-024'],
      quantities: [2, 2, 3, 6],
      discounts: [100, 98, 100, 100],
      dirty: true,
    }),
    createScenarioQuote({
      quoteId: 'Q-V2-0002',
      title: '移动短视频协作报价',
      customerIndex: 1,
      status: 'collaboration',
      businessModeCode: 'rental',
      deviceIds: ['DEV-005', 'DEV-014', 'DEV-020'],
      quantities: [1, 2, 2],
      dirty: true,
    }),
    createScenarioQuote({
      quoteId: 'Q-V2-0003',
      title: '双机访谈客户确认版',
      customerIndex: 2,
      status: 'customer_confirmation',
      businessModeCode: 'rental',
      deviceIds: ['DEV-003', 'DEV-006', 'DEV-012', 'DEV-021'],
      quantities: [2, 1, 2, 3],
    }),
    createScenarioQuote({
      quoteId: 'Q-V2-0004',
      title: '企业推流服务待提交',
      customerIndex: 3,
      status: 'pending_submission',
      businessModeCode: 'service',
      deviceIds: ['DEV-017', 'DEV-022', 'DEV-024'],
      quantities: [1, 1, 4],
      salesOrderId: '2026081100000004',
    }),
    createScenarioQuote({
      quoteId: 'Q-V2-0005',
      title: '户外活动提交中',
      customerIndex: 4,
      status: 'submitting',
      businessModeCode: 'rental',
      deviceIds: ['DEV-004', 'DEV-018', 'DEV-014'],
      quantities: [2, 1, 2],
      activeOperationId: 'OP-DEMO-0005',
      salesOrderId: '2026081100000005',
    }),
    createScenarioQuote({
      quoteId: 'Q-V2-0006',
      title: '课程录制已成交',
      customerIndex: 5,
      status: 'success',
      businessModeCode: 'trade',
      deviceIds: ['DEV-002', 'DEV-008', 'DEV-011', 'DEV-022'],
      quantities: [2, 2, 3, 1],
      salesOrderId: '2026081100000006',
    }),
    createScenarioQuote({
      quoteId: 'Q-V2-0007',
      title: '导播工作站提交失败',
      customerIndex: 6,
      status: 'failed',
      businessModeCode: 'service',
      deviceIds: ['DEV-013', 'DEV-017', 'DEV-023'],
      quantities: [1, 2, 2],
      failureReason: 'MOCK-ERP-409：客户信用额度不足，请修改付款方式后重试',
      salesOrderId: '2026081100000007',
    }),
    createScenarioQuote({
      quoteId: 'Q-V2-0008',
      title: '会展租赁已作废',
      customerIndex: 7,
      status: 'void',
      businessModeCode: 'rental',
      deviceIds: ['DEV-009', 'DEV-010', 'DEV-015'],
      quantities: [2, 4, 3],
    }),
    createScenarioQuote({
      quoteId: 'Q-V2-0009',
      title: '课程录制归档版本 V2',
      customerIndex: 5,
      status: 'archived',
      businessModeCode: 'trade',
      deviceIds: ['DEV-002', 'DEV-008', 'DEV-011', 'DEV-023'],
      quantities: [3, 3, 4, 2],
      version: 2,
      sourceQuoteId: 'Q-V2-0006',
      sourceVersion: 1,
      sourceReceiptId: 'RECEIPT-0002',
      salesOrderId: '2026081100000009',
    }),
  ];
  const demoStatusSequence: QuoteStatus[] = [
    'draft',
    'collaboration',
    'customer_confirmation',
    'pending_submission',
    'submitting',
    'success',
    'failed',
    'void',
    'archived',
  ];
  for (let index = 10; index <= 51; index += 1) {
    const status = demoStatusSequence[(index - 10) % demoStatusSequence.length];
    scenarioQuotes.push(
      createScenarioQuote({
        quoteId: `Q-V2-${String(index).padStart(4, '0')}`,
        title: `状态演示报价 ${String(index).padStart(2, '0')} · ${quoteStatusLabels[status]}`,
        customerIndex: (index - 1) % PRODUCT_CATALOG_SEED.customers.length,
        status,
        businessModeCode: index % 3 === 0 ? 'trade' : 'rental',
        deviceIds: [
          `DEV-${String(((index * 2) % 36) + 1).padStart(3, '0')}`,
          `DEV-${String(((index * 2 + 7) % 36) + 1).padStart(3, '0')}`,
        ],
        quantities: [1 + (index % 3), 1],
        dirty: status === 'draft',
        activeOperationId:
          status === 'submitting'
            ? `OP-MOCK-${String(index).padStart(4, '0')}`
            : undefined,
        failureReason:
          status === 'failed'
            ? `Mock 演示失败：状态场景 ${String(index).padStart(2, '0')}`
            : undefined,
        salesOrderId: `20260811${String(index).padStart(8, '0')}`,
      }),
    );
  }
  const blankQuote = createBlankQuote(
    'Q-V2-NEW-0001',
    1,
    DEFAULT_QUOTE_FIELD_KEYS,
    PRODUCT_CATALOG_SEED.businessModes,
  );
  const quotes = Object.fromEntries(
    [blankQuote, ...scenarioQuotes].map((quote) => [quote.quoteId, quote]),
  );
  const state: CpqState = {
    schemaVersion: 5,
    currentRoleId: 'ROLE-SUPER-ADMIN',
    role: 'super_admin',
    currentActorUserId: 'U-000',
    activeView: 'workspace',
    workspacePicker: null,
    activeQuoteId: blankQuote.quoteId,
    openQuoteIds: [blankQuote.quoteId],
    quotes,
    catalog: createProductCatalog(),
    shareStyles: createDefaultShareStyles(DEMO_TIME),
    negotiationDrafts: [],
    shares: [
      {
        id: 'SHARE-0001',
        token: 'demo-active-0001',
        quoteId: 'Q-V2-0002',
        quoteVersion: 1,
        allowSelection: true,
        status: 'active',
        createdAt: DEMO_TIME,
      },
      {
        id: 'SHARE-0002',
        token: 'demo-receipt-0002',
        quoteId: 'Q-V2-0003',
        quoteVersion: 1,
        allowSelection: true,
        status: 'receipt_received',
        createdAt: DEMO_TIME,
      },
      {
        id: 'SHARE-0003',
        token: 'demo-readonly-0003',
        quoteId: 'Q-V2-0006',
        quoteVersion: 1,
        allowSelection: false,
        status: 'active',
        createdAt: DEMO_TIME,
      },
      {
        id: 'SHARE-0004',
        token: 'demo-revoked-0004',
        quoteId: 'Q-V2-0008',
        quoteVersion: 1,
        allowSelection: false,
        status: 'revoked',
        createdAt: DEMO_TIME,
      },
      {
        id: 'SHARE-0012',
        token: 'demo-paused-0012',
        quoteId: 'Q-V2-0016',
        quoteVersion: 1,
        allowSelection: false,
        status: 'active',
        createdAt: DEMO_TIME,
      },
      {
        id: 'SHARE-0013',
        token: 'demo-expired-0013',
        quoteId: 'Q-V2-0009',
        quoteVersion: 2,
        allowSelection: false,
        status: 'active',
        createdAt: DEMO_TIME,
      },
      ...additionalFeedbackFixtures.map((fixture) => ({
        id: `SHARE-${String(fixture.sequence - 1).padStart(4, '0')}`,
        token: `demo-update-${String(fixture.sequence).padStart(4, '0')}`,
        quoteId: fixture.quoteId,
        quoteVersion: 1,
        allowSelection: true,
        status: 'receipt_received' as const,
        createdAt: fixture.createdAt,
      })),
    ],
    receipts: [
      {
        id: 'RECEIPT-0001',
        shareId: 'SHARE-0002',
        sourceQuoteId: 'Q-V2-0003',
        sourceVersion: 1,
        companyOrName: '光合文化采购组',
        phone: '13821235525',
        note: '镜头数量增加到 3 台，并希望周五前交付',
        selectionSnapshot: {
          kind: 'legacy-unrecoverable',
          reason: 'fixture pending initialization',
        },
        status: 'submitted',
        createdAt: DEMO_TIME,
      },
      {
        id: 'RECEIPT-0002',
        shareId: 'SHARE-0003',
        sourceQuoteId: 'Q-V2-0006',
        sourceVersion: 1,
        companyOrName: '云图电商直播部',
        phone: '13755190472',
        note: '确认升级监视器，已转为 V2',
        selectionSnapshot: {
          kind: 'legacy-unrecoverable',
          reason: 'fixture pending initialization',
        },
        status: 'converted',
        convertedQuoteId: 'Q-V2-0009',
        createdAt: DEMO_TIME,
      },
      {
        id: 'RECEIPT-0003',
        shareId: 'SHARE-0001',
        sourceQuoteId: MULTI_ROUND_FEEDBACK_MASTER_ID,
        sourceVersion: 1,
        companyOrName: '悦动视觉陈静',
        phone: '13847260428',
        note: '希望稳定器增加一套',
        selectionSnapshot: {
          kind: 'legacy-unrecoverable',
          reason: 'fixture pending initialization',
        },
        status: 'converted',
        convertedQuoteId: MULTI_ROUND_FEEDBACK_V2_ID,
        createdAt: DEMO_TIME,
      },
      {
        id: 'RECEIPT-0004',
        shareId: 'SHARE-0001',
        sourceQuoteId: MULTI_ROUND_FEEDBACK_V2_ID,
        sourceVersion: 2,
        companyOrName: '悦动视觉陈静',
        phone: '13847260428',
        note: '客户补充：删除索尼 FX3，新增 Sony A7M4，并确认周末可取货',
        selectionSnapshot: {
          kind: 'legacy-unrecoverable',
          reason: 'fixture pending initialization',
        },
        status: 'converted',
        convertedQuoteId: MULTI_ROUND_FEEDBACK_V3_ID,
        createdAt: '2026-08-12T09:30:00.000Z',
      },
      {
        id: 'RECEIPT-0005',
        shareId: 'SHARE-0001',
        sourceQuoteId: MULTI_ROUND_FEEDBACK_V3_ID,
        sourceVersion: 3,
        companyOrName: '悦动视觉陈静',
        phone: '13847260428',
        note: '客户再次反馈：增加一支麦克风，并要求重新确认配置',
        selectionSnapshot: {
          kind: 'legacy-unrecoverable',
          reason: 'fixture pending initialization',
        },
        status: 'submitted',
        createdAt: '2026-08-13T14:10:00.000Z',
      },
      ...additionalFeedbackFixtures.map((fixture) => ({
        id: `RECEIPT-${String(fixture.sequence).padStart(4, '0')}`,
        shareId: `SHARE-${String(fixture.sequence - 1).padStart(4, '0')}`,
        sourceQuoteId: fixture.quoteId,
        sourceVersion: 1,
        companyOrName: fixture.customer,
        phone: fixture.phone,
        note: fixture.note,
        selectionSnapshot: {
          kind: 'legacy-unrecoverable' as const,
          reason: 'fixture pending initialization',
        },
        status: 'submitted' as const,
        customerAction: 'request_change' as const,
        createdAt: fixture.createdAt,
      })),
    ],
    exportTasks: [],
    stockReminders: [
      {
        id: 'STOCK-0001',
        quoteId: 'Q-V2-0001',
        quoteVersionId: 'Q-V2-0001',
        quoteItemId: 'Q-V2-0001-ITEM-01',
        skuId: quotes['Q-V2-0001'].items[0].sku,
        deviceId: quotes['Q-V2-0001'].items[0].deviceId,
        skuSnapshot: 'SKU-导0013',
        warehouseId: null,
        inventoryRunId: 'mock-inventory-r1',
        status: 'OPEN',
        occurrenceCount: 1,
        requiredQuantity: 2,
        availableQuantity: 0,
        rowVersion: 1,
        createdAt: DEMO_TIME,
        resolvedAt: null,
        projectionSource: 'local-storage-mock',
      },
      {
        id: 'STOCK-0002',
        quoteId: 'Q-V2-0005',
        quoteVersionId: 'Q-V2-0005',
        quoteItemId: 'Q-V2-0005-ITEM-02',
        skuId: quotes['Q-V2-0005'].items[1].sku,
        deviceId: quotes['Q-V2-0005'].items[1].deviceId,
        skuSnapshot: 'SKU-网0018',
        warehouseId: null,
        inventoryRunId: 'mock-inventory-r2',
        status: 'OPEN',
        occurrenceCount: 1,
        requiredQuantity: 1,
        availableQuantity: 0,
        rowVersion: 1,
        createdAt: DEMO_TIME,
        resolvedAt: null,
        projectionSource: 'local-storage-mock',
      },
      {
        id: 'STOCK-0003',
        quoteId: 'Q-V2-0003',
        quoteVersionId: 'Q-V2-0003',
        quoteItemId: 'Q-V2-0003-ITEM-01',
        skuId: quotes['Q-V2-0003'].items[0].sku,
        deviceId: quotes['Q-V2-0003'].items[0].deviceId,
        skuSnapshot: 'SKU-相0003',
        warehouseId: null,
        inventoryRunId: 'mock-inventory-r2',
        status: 'RESOLVED',
        occurrenceCount: 1,
        requiredQuantity: 2,
        availableQuantity: 1,
        rowVersion: 1,
        createdAt: DEMO_TIME,
        resolvedAt: DEMO_TIME,
        projectionSource: 'local-storage-mock',
      },
      {
        id: 'STOCK-0004',
        quoteId: 'Q-V2-0007',
        quoteVersionId: 'Q-V2-0007',
        quoteItemId: 'Q-V2-0007-ITEM-02',
        skuId: quotes['Q-V2-0007'].items[1].sku,
        deviceId: quotes['Q-V2-0007'].items[1].deviceId,
        skuSnapshot: 'SKU-机0017',
        warehouseId: null,
        inventoryRunId: 'mock-inventory-r3',
        status: 'RESOLVED',
        occurrenceCount: 1,
        requiredQuantity: 2,
        availableQuantity: 1,
        rowVersion: 1,
        createdAt: DEMO_TIME,
        resolvedAt: DEMO_TIME,
        projectionSource: 'local-storage-mock',
      },
    ],
    activities: [
      {
        id: 'ACT-0001',
        quoteId: 'Q-V2-0002',
        quoteVersion: 1,
        type: 'colleague_send',
        status: 'success',
        detail: '已发送给刘经理、吴恩帅；留言：请确认档期',
        createdAt: DEMO_TIME,
      },
      {
        id: 'ACT-0002',
        quoteId: 'Q-V2-0003',
        quoteVersion: 1,
        type: 'export',
        status: 'success',
        detail: '已生成 PDF 客户版',
        createdAt: DEMO_TIME,
      },
      {
        id: 'ACT-0003',
        quoteId: 'Q-V2-0004',
        quoteVersion: 1,
        type: 'colleague_send',
        status: 'partial_failure',
        detail: '刘洋发送成功；陈静账号停用未送达',
        createdAt: DEMO_TIME,
      },
      {
        id: 'ACT-0004',
        quoteId: 'Q-V2-0006',
        quoteVersion: 1,
        type: 'export',
        status: 'success',
        detail: '已生成 Excel 内部成本版',
        createdAt: DEMO_TIME,
      },
      {
        id: 'ACT-0005',
        quoteId: 'Q-V2-0007',
        quoteVersion: 1,
        type: 'export',
        status: 'partial_failure',
        detail: 'PDF 成功，图片渲染失败，可重试',
        createdAt: DEMO_TIME,
      },
      {
        id: 'ACT-0006',
        quoteId: 'Q-V2-0009',
        quoteVersion: 2,
        type: 'colleague_send',
        status: 'success',
        detail: '已发送归档通知给客户经理',
        createdAt: DEMO_TIME,
      },
    ],
    audits: [
      {
        id: 'AUD-SEED-001',
        quoteId: 'Q-V2-0006',
        quoteVersion: 1,
        action: 'submission.completed',
        result: 'success',
        detail: '模拟正式提交成功并生成销售订单',
        createdAt: DEMO_TIME,
      },
      {
        id: 'AUD-SEED-002',
        quoteId: 'Q-V2-0007',
        quoteVersion: 1,
        action: 'submission.completed',
        result: 'failure',
        detail: '客户信用额度不足',
        createdAt: DEMO_TIME,
      },
      {
        id: 'AUD-SEED-003',
        quoteId: 'Q-V2-0005',
        quoteVersion: 1,
        action: 'submission.started',
        result: 'info',
        detail: '提交任务 OP-DEMO-0005 处理中',
        createdAt: DEMO_TIME,
      },
      {
        id: 'AUD-SEED-004',
        quoteId: 'Q-V2-0004',
        quoteVersion: 1,
        action: 'submission.canceled',
        result: 'canceled',
        detail: '用户取消上一轮提交并返回待提交',
        createdAt: DEMO_TIME,
      },
      {
        id: 'AUD-SEED-005',
        quoteId: 'Q-V2-0003',
        quoteVersion: 1,
        action: 'receipt.received',
        result: 'success',
        detail: '客户回执已绑定来源版本',
        createdAt: DEMO_TIME,
      },
      {
        id: 'AUD-SEED-006',
        quoteId: 'Q-V2-0009',
        quoteVersion: 2,
        action: 'quote.version.created',
        result: 'success',
        detail: '从 Q-V2-0006 V1 创建归档版本',
        createdAt: DEMO_TIME,
      },
      {
        id: 'AUD-SEED-007',
        quoteId: 'Q-V2-0001',
        quoteVersion: 1,
        action: 'stock.reminder.sent',
        result: 'info',
        detail: 'ATEM Mini Pro ISO 库存为 0，已提醒采购',
        createdAt: DEMO_TIME,
      },
      {
        id: 'AUD-SEED-008',
        quoteId: 'Q-V2-0008',
        quoteVersion: 1,
        action: 'quote.voided',
        result: 'success',
        detail: '客户取消会展活动，报价已作废',
        createdAt: DEMO_TIME,
      },
    ],
    temporaryDevices: [
      {
        id: 'TEMP-0001',
        name: '临时直播编码器',
        sku: 'TEMP-ENC-001',
        price: 2680,
        stock: 2,
        remark: '演示临时设备，可编辑后加入报价',
      },
      {
        id: 'TEMP-0002',
        name: '临时卫星背包',
        sku: 'TEMP-SAT-002',
        price: 16800,
        stock: 0,
        remark: '零库存高价值设备场景',
      },
      {
        id: 'TEMP-0003',
        name: '移动电源套装',
        sku: 'TEMP-POWER-003',
        price: 399,
        stock: 8,
        remark: '户外供电，库存充足',
      },
      {
        id: 'TEMP-0004',
        name: '定制背景板 3×6m',
        sku: 'TEMP-BG-004',
        price: 2400,
        stock: 1,
        remark: '一次性定制物料，不可平替',
      },
      {
        id: 'TEMP-0005',
        name: '现场速记服务',
        sku: 'TEMP-SVC-005',
        price: 1800,
        stock: 99,
        remark: '以临时设备形式演示服务项',
      },
      {
        id: 'TEMP-0006',
        name: '备用 5G 流量包',
        sku: 'TEMP-5G-006',
        price: 299,
        stock: 50,
        remark: '低单价高库存边界',
      },
      {
        id: 'TEMP-0007',
        name: '海外制式转换器',
        sku: 'TEMP-CONV-007',
        price: 1280,
        stock: 2,
        remark: '仅海外项目使用',
      },
      {
        id: 'TEMP-0008',
        name: '客户自备设备占位',
        sku: 'TEMP-BYO-008',
        price: 0,
        stock: 1,
        remark: '零价格但需进入清单的边界场景',
      },
    ],
    stockOverrides: {},
    stockSyncRevision: 3,
    nextSequence: 1000,
  };
  state.quotes[MULTI_ROUND_FEEDBACK_V2_ID] = createDemoFeedbackVersion(state, {
    sourceQuoteId: MULTI_ROUND_FEEDBACK_MASTER_ID,
    quoteId: MULTI_ROUND_FEEDBACK_V2_ID,
    version: 2,
    sourceReceiptId: 'RECEIPT-0003',
    createdAt: '2026-08-11T15:20:00.000Z',
  });
  state.quotes[MULTI_ROUND_FEEDBACK_V3_ID] = createDemoFeedbackVersion(state, {
    sourceQuoteId: MULTI_ROUND_FEEDBACK_V2_ID,
    quoteId: MULTI_ROUND_FEEDBACK_V3_ID,
    version: 3,
    sourceReceiptId: 'RECEIPT-0004',
    createdAt: '2026-08-12T16:30:00.000Z',
  });
  const defaultShareStyle = state.shareStyles.find((style) => style.isDefault);
  if (!defaultShareStyle) throw new Error('演示分享样式缺少默认样式');
  const defaultShareCombination = state.catalog.shareFieldCombinations.find(
    (combination) =>
      combination.enabled &&
      combination.channels.includes('channel:public-link'),
  );
  if (!defaultShareCombination)
    throw new Error('演示分享字段组合缺少公开链接组合');
  const fieldValuePort = createDisposableFieldValuePort();
  state.shares = state.shares.map((share) => {
    const quote = state.quotes[share.quoteId];
    if (!quote) return share;
    const projection = buildQuoteChannelProjection(
      state,
      quote.quoteId,
      'channel:public-link',
      fieldValuePort,
      defaultShareCombination.fieldIds,
      {
        customerId: quote.customer.customerId ?? '',
        customerNo: quote.customer.customerNo,
        name: quote.customer.name,
        contact: quote.customer.contact,
        phone: quote.customer.phone,
      },
    );
    const receiptProjection = buildQuoteChannelProjection(
      state,
      quote.quoteId,
      'channel:public-link',
      fieldValuePort,
      undefined,
      {
        customerId: quote.customer.customerId ?? '',
        customerNo: quote.customer.customerNo,
        name: quote.customer.name,
        contact: quote.customer.contact,
        phone: quote.customer.phone,
      },
    );
    const combinationSnapshot = shareFieldCombinationSnapshot(
      state,
      defaultShareCombination,
      projection,
    );
    const frozenQuotation = freezePublicQuotationProjections(
      state,
      projection,
      combinationSnapshot,
    );
    if (
      !projection.customerSnapshot ||
      projection.validUntilSnapshot === undefined ||
      projection.overallDiscountSnapshot === undefined
    )
      throw new Error('演示分享公开投影缺少冻结事实');
    const businessSectionsSnapshot = quoteOutputBusinessSections(quote, state);
    return {
      ...share,
      snapshotPolicy: 'published-channel-v1' as const,
      customerSnapshot: structuredClone(projection.customerSnapshot),
      validUntilSnapshot: projection.validUntilSnapshot,
      itemsSnapshot: structuredClone(receiptProjection.publicItems),
      overallDiscountSnapshot: receiptProjection.overallDiscountSnapshot,
      combinationSnapshot,
      quotationListProjection: frozenQuotation.quotationListProjection,
      headerProjection: frozenQuotation.headerProjection,
      ...(businessSectionsSnapshot.length > 0
        ? { businessSectionsSnapshot }
        : {}),
      selectionOptionsSnapshot: structuredClone(
        receiptProjection.selectionOptions,
      ),
      shareStyleSnapshot: createShareStyleSnapshot(
        defaultShareStyle,
        share.createdAt,
      ),
    };
  });
  state.receipts = state.receipts.map((receipt) => {
    const share = state.shares.find((item) => item.id === receipt.shareId);
    const source = state.quotes[receipt.sourceQuoteId];
    const converted = receipt.convertedQuoteId
      ? state.quotes[receipt.convertedQuoteId]
      : undefined;
    const items =
      (source?.version === receipt.sourceVersion ? source.items : undefined) ??
      converted?.items ??
      (share?.itemsSnapshot?.every(isReceiptCapableShareItem)
        ? share.itemsSnapshot.filter(isReceiptCapableShareItem)
        : undefined);
    const overallDiscount =
      (source?.version === receipt.sourceVersion
        ? source.overallDiscount
        : undefined) ??
      converted?.overallDiscount ??
      share?.overallDiscountSnapshot;
    return items && overallDiscount !== undefined
      ? {
          ...receipt,
          ...(share?.customerSnapshot?.customerId
            ? { customerId: share.customerSnapshot.customerId }
            : {}),
          selectionSnapshot: fixtureReceiptSnapshotWithDemoChange(
            receipt.id,
            items,
            overallDiscount,
          ),
        }
      : receipt;
  });
  // v4 fixtures are imported once into the master-link/publication model.
  // The old `shares` array remains only for fixture compatibility; all new
  // UI writes go through negotiationLinks/publications.
  state.negotiationLinks = state.shares.map((share) => ({
    id: `LINK-${share.id.replace('SHARE-', '')}`,
    token: share.token,
    quoteMasterId: state.quotes[share.quoteId]?.masterId ?? share.quoteId,
    subjectId: 'default-subject',
    skinId: 'default-skin',
    subjectLabel: '默认公司主体',
    skinLabel: '默认皮肤',
    customerId: state.quotes[share.quoteId]?.customer.customerId ?? '',
    customerName: state.quotes[share.quoteId]?.customer.name ?? '演示客户',
    customerPhone: state.quotes[share.quoteId]?.customer.phone ?? '',
    allowSelection: share.allowSelection,
    lifecycle: share.status === 'revoked' ? 'revoked' : 'active',
    createdAt: share.createdAt,
    updatedAt: share.createdAt,
  }));
  const pausedDemoLink = state.negotiationLinks.find(
    (link) => link.token === 'demo-paused-0012',
  );
  if (pausedDemoLink) pausedDemoLink.lifecycle = 'paused';
  const expiredDemoLink = state.negotiationLinks.find(
    (link) => link.token === 'demo-expired-0013',
  );
  if (expiredDemoLink) {
    expiredDemoLink.lifecycle = 'expired';
    expiredDemoLink.expiresAt = '2026-07-31T23:59:59.999Z';
  }
  state.publications = state.shares.flatMap((share) => {
    const quote = state.quotes[share.quoteId];
    const link = state.negotiationLinks?.find(
      (item) => item.token === share.token,
    );
    if (
      !quote ||
      !link ||
      !share.itemsSnapshot ||
      share.overallDiscountSnapshot === undefined
    )
      return [];
    const publication = {
      id: `PUB-${share.id.replace('SHARE-', '')}`,
      linkId: link.id,
      quoteMasterId: link.quoteMasterId,
      quoteId: share.quoteId,
      quoteVersion: share.quoteVersion,
      snapshotPolicy: share.snapshotPolicy ?? ('legacy-unavailable' as const),
      customerSnapshot: structuredClone(
        share.customerSnapshot ?? {
          customerId: quote.customer.customerId ?? '',
          customerNo: quote.customer.customerNo,
          name: quote.customer.name,
          contact: quote.customer.contact,
          phone: quote.customer.phone,
        },
      ),
      validUntilSnapshot: share.validUntilSnapshot ?? quote.validUntil,
      businessModeLabelSnapshot: quote.businessModeLabelSnapshot,
      itemsSnapshot: share.itemsSnapshot.map((item) => structuredClone(item)),
      overallDiscountSnapshot: share.overallDiscountSnapshot,
      publicFieldSnapshot: [],
      selectionOptionsSnapshot: structuredClone(
        share.selectionOptionsSnapshot ?? [],
      ),
      allowSelection: share.allowSelection,
      combinationSnapshot: structuredClone(share.combinationSnapshot),
      quotationListProjection: structuredClone(share.quotationListProjection),
      headerProjection: structuredClone(share.headerProjection),
      ...(share.businessSectionsSnapshot
        ? {
            businessSectionsSnapshot: structuredClone(
              share.businessSectionsSnapshot,
            ),
          }
        : {}),
      shareStyleSnapshot: structuredClone(
        share.shareStyleSnapshot ??
          createShareStyleSnapshot(defaultShareStyle, share.createdAt),
      ),
      createdAt: share.createdAt,
    };
    link.currentPublicationId = publication.id;
    return [publication];
  });
  const multiRoundFeedbackLink = state.negotiationLinks.find(
    (link) => link.quoteMasterId === MULTI_ROUND_FEEDBACK_MASTER_ID,
  );
  if (!multiRoundFeedbackLink) {
    throw new Error('多轮反馈演示缺少固定客户链接');
  }
  const createFeedbackPublication = (
    id: string,
    quoteId: string,
    createdAt: string,
  ): NonNullable<CpqState['publications']>[number] => {
    const quote = state.quotes[quoteId];
    if (!quote) throw new Error(`多轮反馈演示报价 ${quoteId} 不存在`);
    const customerSnapshot = {
      customerId: quote.customer.customerId ?? '',
      customerNo: quote.customer.customerNo,
      name: quote.customer.name,
      contact: quote.customer.contact,
      phone: quote.customer.phone,
    };
    const projection = buildQuoteChannelProjection(
      state,
      quoteId,
      'channel:public-link',
      fieldValuePort,
      defaultShareCombination.fieldIds,
      customerSnapshot,
    );
    const receiptProjection = buildQuoteChannelProjection(
      state,
      quoteId,
      'channel:public-link',
      fieldValuePort,
      undefined,
      customerSnapshot,
    );
    const combinationSnapshot = shareFieldCombinationSnapshot(
      state,
      defaultShareCombination,
      projection,
    );
    const frozenQuotation = freezePublicQuotationProjections(
      state,
      projection,
      combinationSnapshot,
    );
    if (
      !projection.customerSnapshot ||
      projection.validUntilSnapshot === undefined ||
      receiptProjection.overallDiscountSnapshot === undefined
    ) {
      throw new Error(`多轮反馈演示发布 ${id} 缺少冻结事实`);
    }
    const businessSectionsSnapshot = quoteOutputBusinessSections(quote, state);
    return {
      id,
      linkId: multiRoundFeedbackLink.id,
      quoteMasterId: MULTI_ROUND_FEEDBACK_MASTER_ID,
      quoteId,
      quoteVersion: quote.version,
      snapshotPolicy: 'published-channel-v1',
      customerSnapshot: structuredClone(projection.customerSnapshot),
      validUntilSnapshot: projection.validUntilSnapshot,
      businessModeLabelSnapshot: quote.businessModeLabelSnapshot,
      itemsSnapshot: structuredClone(receiptProjection.publicItems),
      overallDiscountSnapshot: receiptProjection.overallDiscountSnapshot,
      publicFieldSnapshot: structuredClone(projection.publicFieldSnapshot),
      selectionOptionsSnapshot: structuredClone(
        receiptProjection.selectionOptions,
      ),
      allowSelection: true,
      combinationSnapshot,
      quotationListProjection: frozenQuotation.quotationListProjection,
      headerProjection: frozenQuotation.headerProjection,
      ...(businessSectionsSnapshot.length > 0
        ? { businessSectionsSnapshot }
        : {}),
      shareStyleSnapshot: createShareStyleSnapshot(
        defaultShareStyle,
        createdAt,
      ),
      createdAt,
    };
  };
  state.publications.push(
    createFeedbackPublication(
      MULTI_ROUND_FEEDBACK_V2_PUBLICATION_ID,
      MULTI_ROUND_FEEDBACK_V2_ID,
      '2026-08-11T15:20:00.000Z',
    ),
    createFeedbackPublication(
      MULTI_ROUND_FEEDBACK_V3_PUBLICATION_ID,
      MULTI_ROUND_FEEDBACK_V3_ID,
      '2026-08-12T16:30:00.000Z',
    ),
  );
  multiRoundFeedbackLink.currentPublicationId =
    MULTI_ROUND_FEEDBACK_V3_PUBLICATION_ID;
  multiRoundFeedbackLink.updatedAt = '2026-08-12T16:30:00.000Z';
  const feedbackBindingByReceiptId = {
    'RECEIPT-0003': {
      sourceQuoteId: MULTI_ROUND_FEEDBACK_MASTER_ID,
      sourceVersion: 1,
      publicationId: 'PUB-0001',
      dispositionAt: '2026-08-11T15:20:00.000Z',
    },
    'RECEIPT-0004': {
      sourceQuoteId: MULTI_ROUND_FEEDBACK_V2_ID,
      sourceVersion: 2,
      publicationId: MULTI_ROUND_FEEDBACK_V2_PUBLICATION_ID,
      dispositionAt: '2026-08-12T16:30:00.000Z',
    },
    'RECEIPT-0005': {
      sourceQuoteId: MULTI_ROUND_FEEDBACK_V3_ID,
      sourceVersion: 3,
      publicationId: MULTI_ROUND_FEEDBACK_V3_PUBLICATION_ID,
    },
  } as const;
  state.receipts = state.receipts.map((receipt) => {
    const binding =
      feedbackBindingByReceiptId[
        receipt.id as keyof typeof feedbackBindingByReceiptId
      ];
    if (!binding) return receipt;
    const isProcessed = receipt.id !== 'RECEIPT-0005';
    return {
      ...receipt,
      shareId: multiRoundFeedbackLink.id,
      sourceQuoteId: binding.sourceQuoteId,
      sourceVersion: binding.sourceVersion,
      customerId: multiRoundFeedbackLink.customerId,
      quoteMasterId: MULTI_ROUND_FEEDBACK_MASTER_ID,
      linkId: multiRoundFeedbackLink.id,
      publicationId: binding.publicationId,
      customerAction: 'request_change' as const,
      customerIdentity: `${multiRoundFeedbackLink.customerName}|${multiRoundFeedbackLink.customerPhone}`,
      ...(isProcessed
        ? {
            disposition: 'adopted' as const,
            dispositionAt:
              'dispositionAt' in binding
                ? binding.dispositionAt
                : receipt.createdAt,
            dispositionNote: '已更新并生成下一分享版本',
          }
        : {}),
    };
  });
  return state;
}

let cachedDemoState: CpqState | null = null;

/**
 * The demo fixture is immutable seed data from the caller's perspective.
 * Build its expensive catalog/projection graph once, then hand each caller
 * an isolated clone so tests and disposable tabs cannot share mutations.
 */
export function createDemoState(): CpqState {
  cachedDemoState ??= buildDemoState();
  return structuredClone(cachedDemoState);
}

export function createQuote(state: CpqState): CpqState {
  if (state.openQuoteIds.length >= 10)
    throw new Error('最多同时打开 10 个报价');
  const next = cloneState(state);
  const quoteId = `Q-V2-${String(next.nextSequence).padStart(4, '0')}`;
  next.nextSequence += 1;
  const definitions = new Map(
    next.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
  );
  const selectedEntries = next.catalog.quoteFieldPool
    .filter((entry) => {
      const field = definitions.get(entry.fieldKey);
      return (
        entry.enabled &&
        (entry.mandatory || entry.defaultVisible) &&
        field?.enabled &&
        field.governance.classification === 'ordinary'
      );
    })
    .sort((left, right) => left.sortOrder - right.sortOrder);
  next.quotes[quoteId] = {
    ...createBlankQuote(quoteId, 1, [], next.catalog.businessModes),
    customColumns: selectedEntries
      .filter((entry) => entry.placement === 'quote-line')
      .map((entry) => entry.fieldKey),
    fieldSelections: selectedEntries.map((entry) => ({
      fieldKey: entry.fieldKey,
      placement: entry.placement,
    })),
    customColumnSnapshot: selectedEntries.flatMap((entry) => {
      const field = definitions.get(entry.fieldKey);
      return field
        ? [
            {
              key: field.key,
              label: entry.displayLabel,
              type: field.type,
              width: field.width,
              note: field.note,
              options: field.options?.map((option) => ({ ...option })),
              sourceKind: field.sourceKind,
              fieldKey: field.governance.fieldKey,
              definitionVersion: field.governance.definitionVersion,
              formulaVersion: field.governance.formulaVersion,
              ...(field.calculation
                ? { calculation: structuredClone(field.calculation) }
                : {}),
              placement: entry.placement,
            },
          ]
        : [];
    }),
  };
  next.quotes[quoteId] = bindLatestSelectedCalculations(
    next,
    next.quotes[quoteId],
  );
  const createdAt = new Date().toISOString();
  next.quotes[quoteId].createdAt = createdAt;
  next.quotes[quoteId].updatedAt = createdAt;
  next.openQuoteIds.push(quoteId);
  next.activeQuoteId = quoteId;
  return appendAudit(
    next,
    'quote.created',
    'success',
    '已新建报价',
    next.quotes[quoteId],
  );
}

export function createQuoteVersion(
  state: CpqState,
  sourceQuoteId: string,
  options?: { version?: number; sourceReceiptId?: string },
): CpqState {
  const source = requireQuote(state, sourceQuoteId);
  const selectablePool = new Map(
    state.catalog.quoteFieldPool
      .filter(
        (entry) => entry.enabled && isQuoteDefinitionBound(entry.fieldKey),
      )
      .map((entry) => [entry.fieldKey, entry]),
  );
  const sourceSelections = quoteFieldSelections(source);
  const hasInvalidCurrentColumn = sourceSelections.some(
    (selection) =>
      selectablePool.get(selection.fieldKey)?.placement !==
        selection.placement ||
      !canProjectGovernedQuoteField(
        state.role,
        source,
        selection.fieldKey,
        state.catalog.quoteFieldDefinitions,
        state,
      ),
  );
  if (source.status === 'draft' && hasInvalidCurrentColumn) {
    throw new Error('字段池、来源或当前角色权限不足，拒绝创建新版本');
  }
  const compatibleSourceSelections = sourceSelections.filter(
    (selection) =>
      selectablePool.get(selection.fieldKey)?.placement ===
        selection.placement &&
      canProjectGovernedQuoteField(
        state.role,
        source,
        selection.fieldKey,
        state.catalog.quoteFieldDefinitions,
        state,
      ),
  );
  const currentSelections =
    compatibleSourceSelections.length > 0
      ? compatibleSourceSelections
      : DEFAULT_QUOTE_FIELD_KEYS.flatMap((fieldKey) => {
          const entry = selectablePool.get(fieldKey);
          return entry &&
            canProjectGovernedQuoteField(
              state.role,
              source,
              fieldKey,
              state.catalog.quoteFieldDefinitions,
              state,
            )
            ? [{ fieldKey, placement: entry.placement }]
            : [];
        });
  const currentKeys = currentSelections.map((selection) => selection.fieldKey);
  const currentColumns = currentSelections
    .filter((selection) => selection.placement === 'quote-line')
    .map((selection) => selection.fieldKey);
  const currentSnapshot = snapshotQuoteColumns(
    currentKeys,
    state.catalog.quoteFieldDefinitions,
    state.role,
    source,
    state,
  ).map((snapshot) => ({
    ...snapshot,
    label: selectablePool.get(snapshot.key)?.displayLabel ?? snapshot.label,
    placement: selectablePool.get(snapshot.key)?.placement ?? 'quote-line',
  }));
  const next = createQuote(state);
  const quoteId = next.activeQuoteId;
  const version = options?.version ?? source.version + 1;
  const activeSourceMode = isStableBusinessModeCode(source.businessModeCode)
    ? state.catalog.businessModes.find(
        (mode) => mode.code === source.businessModeCode && mode.active,
      )
    : undefined;
  next.quotes[quoteId] = {
    ...structuredClone(source),
    quoteId,
    masterId: source.masterId ?? source.quoteId,
    version,
    sourceQuoteId: source.quoteId,
    sourceVersion: source.version,
    sourceReceiptId: options?.sourceReceiptId,
    versionWorkflow: undefined,
    businessModeLabelSnapshot:
      !options?.sourceReceiptId && activeSourceMode
        ? activeSourceMode.label
        : source.businessModeLabelSnapshot,
    businessModeRequiresReselection: !activeSourceMode,
    title: `${source.customer.name || source.title} V${version}`,
    status: 'draft',
    dirty: true,
    activeOperationId: undefined,
    failureReason: undefined,
    salesOrderId: undefined,
    customColumns: currentColumns,
    fieldSelections: currentSelections,
    customColumnSnapshot: currentSnapshot,
    // 新版本会重新冻结计算事实；不能把来源版本的 quoteId/version
    // 计算快照带入草稿，否则回执导入后的第一次持久化会被 v5 门禁拒绝。
    items: structuredClone(source.items).map((item) => {
      const { calculationSnapshots: _staleSnapshots, ...draftItem } = item;
      return draftItem;
    }),
    createdAt: new Date().toISOString(),
    updatedAt: DEMO_TIME,
  };
  next.quotes[quoteId] = bindLatestSelectedCalculations(
    next,
    next.quotes[quoteId],
  );
  return appendAudit(
    next,
    'quote.version.created',
    'success',
    `从 ${source.quoteId} V${source.version} 创建新版本${options?.sourceReceiptId ? `（回执 ${options.sourceReceiptId}）` : ''}`,
    next.quotes[quoteId],
  );
}

export function createQuoteWorkflowVersion(
  state: CpqState,
  sourceQuoteId: string,
  input: {
    originTab: QuoteDetailTab;
    generationSource: QuoteVersionGenerationSource;
    generationReason: string;
    sourceReceiptId?: string;
    sourceLinkId?: string;
  },
): CpqState {
  const source = requireQuote(state, sourceQuoteId);
  const masterId = source.masterId ?? source.quoteId;
  const nextVersion =
    Math.max(
      ...Object.values(state.quotes)
        .filter(
          (candidate) => (candidate.masterId ?? candidate.quoteId) === masterId,
        )
        .map((candidate) => candidate.version),
      source.version,
    ) + 1;
  const next = createQuoteVersion(state, sourceQuoteId, {
    version: nextVersion,
    sourceReceiptId: input.sourceReceiptId,
  });
  const draft = requireQuote(next, next.activeQuoteId);
  draft.versionWorkflow = {
    originTab: input.originTab,
    generationSource: input.generationSource,
    generationReason: input.generationReason,
    stage: 'draft',
    sourceLinkId: input.sourceLinkId,
  };
  next.quoteManagementDetail = {
    masterId,
    tab: input.originTab,
  };
  next.activeView = 'workspace';
  return next;
}

/**
 * Discards only an unsaved workflow draft. The source version remains the
 * active business fact; the discarded version never enters version history.
 * This is intentionally separate from closeQuote, which only closes a UI Tab.
 */
export function discardQuoteWorkflowDraft(
  state: CpqState,
  quoteId: string,
): CpqState {
  const draft = requireQuote(state, quoteId);
  if (draft.versionWorkflow?.stage !== 'draft' || !draft.sourceQuoteId) {
    throw new Error('只有未发布的工作流草稿可以取消编辑');
  }
  const next = cloneState(state);
  const source = requireQuote(next, draft.sourceQuoteId);
  delete next.quotes[quoteId];
  next.openQuoteIds = next.openQuoteIds.filter((id) => id !== quoteId);
  if (!next.openQuoteIds.includes(source.quoteId)) {
    next.openQuoteIds.push(source.quoteId);
  }
  next.activeQuoteId = source.quoteId;
  next.activeView = 'quotes';
  next.quoteManagementDetail = {
    masterId: source.masterId ?? source.quoteId,
    tab: draft.versionWorkflow.originTab,
  };
  return appendAudit(
    next,
    'quote.version.discarded',
    'info',
    `取消编辑并放弃 V${draft.version} 草稿`,
    source,
  );
}

/** Restore one in-session draft snapshot without changing published history. */
export function restoreDraftQuoteSnapshot(
  state: CpqState,
  quoteId: string,
  snapshot: Quote,
): CpqState {
  const current = requireQuote(state, quoteId);
  if (current.versionWorkflow?.stage !== 'draft') {
    throw new Error('只有当前草稿可以撤回编辑动作');
  }
  if (
    snapshot.quoteId !== quoteId ||
    snapshot.status !== 'draft' ||
    snapshot.versionWorkflow?.stage !== 'draft' ||
    snapshot.version !== current.version ||
    snapshot.sourceQuoteId !== current.sourceQuoteId
  ) {
    throw new Error('撤回快照与当前草稿版本不一致');
  }
  const next = cloneState(state);
  const restored = structuredClone(snapshot);
  restored.dirty = true;
  restored.updatedAt = DEMO_TIME;
  next.quotes[quoteId] = restored;
  return appendAudit(
    next,
    'quote.edit.undo',
    'info',
    '已撤回当前编辑会话的上一步操作',
    restored,
  );
}

export function transitionQuoteVersionWorkflow(
  state: CpqState,
  quoteId: string,
  target: QuoteVersionWorkflowStage,
): CpqState {
  const source = requireQuote(state, quoteId);
  const workflow = source.versionWorkflow;
  if (!workflow) throw new Error('当前报价不是由详情工作流生成的版本');
  if (workflow.stage === target) return state;
  const allowed: Record<
    QuoteVersionWorkflowStage,
    QuoteVersionWorkflowStage[]
  > = {
    draft: ['published'],
    published: [],
  };
  if (!allowed[workflow.stage].includes(target)) {
    throw new Error(`当前版本不能从 ${workflow.stage} 直接变为 ${target}`);
  }
  if (target === 'published') {
    if (source.dirty) throw new Error('请先保存草稿，再更新客户分享链接');
    if (source.items.length === 0)
      throw new Error('报价内容为空，不能更新客户分享链接');
  }
  const next = cloneState(state);
  const quote = requireQuote(next, quoteId);
  const nextWorkflow = quote.versionWorkflow;
  if (!nextWorkflow) throw new Error('当前报价缺少版本工作流上下文');
  const updatedAt = new Date().toISOString();
  quote.versionWorkflow = {
    ...nextWorkflow,
    stage: target,
    ...(target === 'published' ? { publishedAt: updatedAt } : {}),
  };
  quote.updatedAt = updatedAt;
  return appendAudit(
    next,
    'quote.version.workflow.changed',
    'success',
    `版本工作流更新为 ${target}`,
    quote,
  );
}

export function createOrderedRevision(
  state: CpqState,
  sourceQuoteId: string,
): CpqState {
  const source = requireQuote(state, sourceQuoteId);
  if (productStatusForQuote(source) !== 'ordered') {
    throw new Error('仅已下单报价可创建变更版本');
  }
  const next = createQuoteVersion(state, sourceQuoteId);
  const revision = requireQuote(next, next.activeQuoteId);
  revision.salesOrderId = source.salesOrderId;
  revision.dirty = true;
  return appendAudit(
    next,
    'quote.ordered_revision.created',
    'success',
    `保留销售订单 ${source.salesOrderId} 并进入报价变更中`,
    revision,
  );
}

export function closeQuote(state: CpqState, quoteId: string): CpqState {
  if (state.openQuoteIds.length === 1) throw new Error('至少保留一个报价 Tab');
  const next = cloneState(state);
  next.openQuoteIds = next.openQuoteIds.filter((id) => id !== quoteId);
  if (next.activeQuoteId === quoteId) {
    next.activeQuoteId = next.openQuoteIds.at(-1) ?? next.openQuoteIds[0];
  }
  return next;
}

export function updateQuote(
  state: CpqState,
  quoteId: string,
  updater: (quote: Quote) => Quote,
  authorize?: QuoteFieldAuthorization,
  formalCanEdit?: boolean,
): CpqState {
  const sourceQuote = requireQuote(state, quoteId);
  const canViewSource = (fieldId: string) =>
    authorize
      ? authorize(fieldId, 'view')
      : canViewQuoteSourceField(state, sourceQuote, fieldId);
  const canEditSource = (fieldId: string) =>
    authorize
      ? authorize(fieldId, 'edit')
      : canEditQuoteSourceField(state, sourceQuote, fieldId);
  if (sourceQuote.status !== 'draft') {
    throw new Error('非草稿报价只读，不能修改商业内容');
  }
  // Updaters may mutate their input. Isolate this quote while retaining the
  // untouched catalog and other tabs, which can contain thousands of rows.
  const quote = structuredClone(sourceQuote);
  const next = { ...state, quotes: { ...state.quotes } };
  const updatedQuote = updater(quote);
  if (
    formalCanEdit === false &&
    JSON.stringify(updatedQuote) !== JSON.stringify(sourceQuote)
  )
    throw new Error('报价编辑权限不足，未保存变更');
  assertQuoteFragmentInstances(updatedQuote.items);
  assertFragmentMemberCoverage(
    updatedQuote.items,
    sourceQuote.items,
    state.catalog,
  );
  if (updatedQuote.businessModeCode !== sourceQuote.businessModeCode) {
    if (!isStableBusinessModeCode(updatedQuote.businessModeCode)) {
      throw new Error('旧业务模式只读，必须显式重选四个 active code 之一');
    }
    const selected = requireActiveBusinessMode(
      state.catalog.businessModes,
      updatedQuote.businessModeCode,
    );
    if (
      updatedQuote.businessModeLabelSnapshot !== selected.label ||
      updatedQuote.businessModeRequiresReselection
    ) {
      throw new Error('业务模式 code 与冻结 label 快照不一致');
    }
  } else if (
    updatedQuote.businessModeLabelSnapshot !==
      sourceQuote.businessModeLabelSnapshot ||
    updatedQuote.businessModeRequiresReselection !==
      sourceQuote.businessModeRequiresReselection
  ) {
    throw new Error('业务模式快照不可直接改写');
  }
  const activeRoleId = activeRoleIdForState(state);
  const canEditQuote =
    formalCanEdit ??
    Boolean(
      activeRoleId &&
        resolveDisposablePermissionAction(
          state,
          activeRoleId,
          'quotes',
          'quotes:quote.edit',
          'draft',
          quotePermissionContext(state, sourceQuote),
        ),
    );
  const same = (left: unknown, right: unknown) =>
    JSON.stringify(left) === JSON.stringify(right);
  const lockedQuoteFields: Array<keyof Quote> = [
    'quoteId',
    'localDraftGeneration',
    'version',
    'sourceQuoteId',
    'sourceVersion',
    'sourceReceiptId',
    'title',
    'status',
    'createdAt',
    'updatedAt',
    'activeOperationId',
    'failureReason',
    'salesOrderId',
    'source',
    'submittedBy',
    'itemAdjustments',
  ];
  if (
    lockedQuoteFields.some((key) => !same(sourceQuote[key], updatedQuote[key]))
  ) {
    throw new Error('报价标识或状态快照不可通过商业内容更新改写');
  }
  const currentItemIds = sourceQuote.items.map((item) => item.itemId);
  const nextItemIds = updatedQuote.items.map((item) => item.itemId);
  if (
    new Set(currentItemIds).size !== currentItemIds.length ||
    new Set(nextItemIds).size !== nextItemIds.length
  )
    throw new Error('报价行稳定标识重复，未保存变更');
  const currentItems = new Map(
    sourceQuote.items.map((item) => [item.itemId, item]),
  );
  const nextItems = new Map(
    updatedQuote.items.map((item) => [item.itemId, item]),
  );
  const validFragmentScale = (item: QuoteItem): boolean => {
    if (
      item.sourceFragment?.mode === 'quote-customized' &&
      item.containerInstanceId === item.sourceFragment.instanceKey
    ) {
      const instanceId = item.sourceFragment.instanceKey;
      const priorInstance = sourceQuote.containerInstances?.find(
        (entry) => entry.id === instanceId && entry.kind === 'package',
      );
      const nextInstance = updatedQuote.containerInstances?.find(
        (entry) => entry.id === instanceId && entry.kind === 'package',
      );
      const next = nextItems.get(item.itemId);
      const group = sourceQuote.items.filter(
        (entry) =>
          entry.sourceFragment?.mode === 'quote-customized' &&
          entry.sourceFragment.instanceKey === instanceId &&
          entry.containerInstanceId === instanceId,
      );
      if (
        !priorInstance ||
        !nextInstance ||
        !next?.sourceFragment ||
        priorInstance.quantity !== item.sourceFragment.instanceQuantity ||
        nextInstance.quantity !== next.sourceFragment.instanceQuantity ||
        nextInstance.originKind !== priorInstance.originKind ||
        nextInstance.originId !== priorInstance.originId ||
        nextInstance.originRevisionId !== priorInstance.originRevisionId ||
        nextInstance.parentInstanceId !== priorInstance.parentInstanceId ||
        nextInstance.parentMemberId !== priorInstance.parentMemberId ||
        nextInstance.displayName !== priorInstance.displayName ||
        nextInstance.status !== 'customized' ||
        next.sourceFragment.instanceQuantity ===
          item.sourceFragment.instanceQuantity
      )
        return false;
      const priorQuantity = item.sourceFragment.instanceQuantity;
      const nextQuantity = next.sourceFragment.instanceQuantity;
      const nextGroup = updatedQuote.items.filter(
        (entry) =>
          entry.sourceFragment?.mode === 'quote-customized' &&
          entry.sourceFragment.instanceKey === instanceId &&
          entry.containerInstanceId === instanceId,
      );
      if (group.length !== nextGroup.length) return false;
      return group.every((prior) => {
        const priorFragment = prior.sourceFragment;
        const actual = nextItems.get(prior.itemId);
        const actualFragment = actual?.sourceFragment;
        const expectedQuantity =
          prior.quantity % priorQuantity === 0
            ? (prior.quantity / priorQuantity) * nextQuantity
            : Number.NaN;
        return Boolean(
          actual &&
            actualFragment?.mode === 'quote-customized' &&
            actual.containerInstanceId === instanceId &&
            Number.isSafeInteger(expectedQuantity) &&
            actual.quantity === expectedQuantity &&
            actualFragment.instanceKey === instanceId &&
            actualFragment.instanceQuantity === nextQuantity &&
            actualFragment.fragmentKey === `quote-row:${actual.itemId}` &&
            actualFragment.memberKey === priorFragment?.memberKey &&
            actual.fragmentAllocation === undefined,
        );
      });
    }
    if (item.sourceFragment?.mode !== 'complete') return false;
    const group = sourceQuote.items.filter(
      (entry) =>
        entry.sourceFragment?.instanceKey ===
        (item.sourceFragment as QuoteSourceFragment).instanceKey,
    );
    const next = nextItems.get(item.itemId);
    if (
      !next?.sourceFragment ||
      next.sourceFragment.instanceQuantity ===
        item.sourceFragment.instanceQuantity
    )
      return false;
    try {
      const scaled = resizeQuoteFragmentGroup(
        group,
        next.sourceFragment.instanceQuantity,
      );
      return scaled.every((expected) => {
        const actual = nextItems.get(expected.itemId);
        return (
          actual &&
          actual.quantity === expected.quantity &&
          same(actual.sourceFragment, expected.sourceFragment) &&
          same(actual.fragmentAllocation, expected.fragmentAllocation) &&
          same(
            actual.planProductSubtotalSnapshot,
            expected.planProductSubtotalSnapshot,
          ) &&
          same(actual.formalPlanOccurrence, expected.formalPlanOccurrence)
        );
      });
    } catch {
      return false;
    }
  };
  const validContainerEdit = (item: QuoteItem): boolean => {
    const next = nextItems.get(item.itemId);
    return Boolean(
      next && isQuoteEditCustomizedRow(sourceQuote, updatedQuote, item, next),
    );
  };
  // Adding another complete plan scales its frozen per-line amounts. It does
  // not permit changing a source price, relation, or only part of the plan.
  const scaledPlanSources = new Map<string, boolean>();
  const containerById = new Map(
    (sourceQuote.containerInstances ?? []).map((instance) => [
      instance.id,
      instance,
    ]),
  );
  const planRootByContainer = new Map<string, string | null>();
  const planRootIdFor = (item: QuoteItem): string | undefined => {
    let currentId = item.containerInstanceId;
    if (!currentId) return undefined;
    const cached = planRootByContainer.get(currentId);
    let rootId = cached ?? null;
    if (cached === undefined) {
      const path: string[] = [];
      const visited = new Set<string>();
      rootId = null;
      while (currentId) {
        if (visited.has(currentId)) break;
        visited.add(currentId);
        const knownRoot = planRootByContainer.get(currentId);
        if (knownRoot !== undefined) {
          rootId = knownRoot;
          break;
        }
        path.push(currentId);
        const instance = containerById.get(currentId);
        if (!instance) break;
        if (instance.kind === 'plan') {
          rootId = instance.id;
          break;
        }
        currentId = instance.parentInstanceId ?? '';
      }
      for (const id of path) planRootByContainer.set(id, rootId);
    }
    const root = rootId ? containerById.get(rootId) : undefined;
    return root?.kind === 'plan' &&
      root.originKind === 'catalog-plan' &&
      root.originId === item.sourceId &&
      root.originRevisionId === item.sourceRevisionId
      ? root.id
      : undefined;
  };
  const isWholePlanQuantityChange = (item: QuoteItem): boolean => {
    if (item.sourceType !== 'plan' || !item.planId) return false;
    const rootId = planRootIdFor(item);
    if (item.containerInstanceId && !rootId) return false;
    const sourceKey = JSON.stringify([
      item.sourceId,
      item.sourceRevisionId,
      rootId ?? null,
    ]);
    const cached = scaledPlanSources.get(sourceKey);
    if (cached !== undefined) return cached;
    const sameSource = (candidate: QuoteItem) =>
      candidate.sourceType === 'plan' &&
      candidate.sourceId === item.sourceId &&
      candidate.sourceRevisionId === item.sourceRevisionId &&
      (candidate.containerInstanceId
        ? planRootIdFor(candidate) === rootId
        : !item.containerInstanceId && rootId === undefined);
    const group = sourceQuote.items.filter(sameSource);
    const baseQuantity = item.packageRelationQuantitySnapshot ?? 0;
    const priorCount = item.quantity / baseQuantity;
    const nextCount =
      (nextItems.get(item.itemId)?.quantity ?? 0) / baseQuantity;
    const valid =
      Number.isSafeInteger(priorCount) &&
      priorCount > 0 &&
      Number.isSafeInteger(nextCount) &&
      nextCount > 0 &&
      nextCount !== priorCount &&
      updatedQuote.items.filter(sameSource).length === group.length &&
      group.every((prior) => {
        const changed = nextItems.get(prior.itemId);
        const relationQuantity = prior.packageRelationQuantitySnapshot ?? 0;
        return (
          changed !== undefined &&
          relationQuantity > 0 &&
          prior.quantity / relationQuantity === priorCount &&
          changed.quantity / relationQuantity === nextCount
        );
      });
    scaledPlanSources.set(sourceKey, valid);
    return valid;
  };
  if (!same([...currentItems.keys()], [...nextItems.keys()]) && !canEditQuote) {
    throw new Error('报价编辑权限不足，未保存设备行变更');
  }
  const editableItemFields: Array<[keyof QuoteItem, string]> = [
    ['quantity', QUOTE_SOURCE_FIELDS.quantity],
    ['discount', QUOTE_SOURCE_FIELDS.discount],
    ['replacementDeviceId', QUOTE_SOURCE_FIELDS.replacementDeviceId],
    ['remark', QUOTE_SOURCE_FIELDS.remark],
  ];
  const lockedItemFields: Array<keyof QuoteItem> = [
    'sourceFragment',
    'fragmentAllocation',
    'deviceId',
    'name',
    'sku',
    'stock',
    'sourceType',
    'sourceId',
    'sourceRevisionId',
    'sourceName',
    'packageRevisionId',
    'planPackageMembershipId',
    'packageRelationId',
    'packageId',
    'packageItemPriceSnapshot',
    'packageRelationQuantitySnapshot',
    'planId',
    'planDiscountSnapshot',
    'planProductPriceSnapshot',
    'planProductSubtotalSnapshot',
    'planSubtotalSnapshot',
    'planPackagePriceSnapshot',
    'packageMonthlySubtotalSnapshot',
    'packageOriginalPriceSnapshot',
    'packageDiscountedQuoteSnapshot',
  ];
  for (const [itemId, currentItem] of currentItems) {
    const nextItem = nextItems.get(itemId);
    if (!nextItem) continue;
    if (
      lockedItemFields.some((key) => {
        if (
          same(currentItem[key], nextItem[key]) &&
          !(
            key === 'planProductSubtotalSnapshot' &&
            currentItem.planProductSubtotalSnapshot !== undefined &&
            isWholePlanQuantityChange(currentItem)
          )
        )
          return false;
        if (
          (key === 'sourceFragment' ||
            key === 'fragmentAllocation' ||
            key === 'planProductSubtotalSnapshot' ||
            key === 'packageRelationQuantitySnapshot' ||
            key === 'planDiscountSnapshot' ||
            key === 'planProductPriceSnapshot' ||
            key === 'planSubtotalSnapshot' ||
            key === 'packageMonthlySubtotalSnapshot' ||
            key === 'packageDiscountedQuoteSnapshot') &&
          (validFragmentScale(currentItem) || validContainerEdit(currentItem))
        )
          return false;
        if (
          key !== 'planProductSubtotalSnapshot' ||
          currentItem.planProductSubtotalSnapshot === undefined ||
          nextItem.planProductSubtotalSnapshot === undefined ||
          !isWholePlanQuantityChange(currentItem)
        ) {
          return true;
        }
        const priorCount =
          currentItem.quantity /
          (currentItem.packageRelationQuantitySnapshot ?? 0);
        const nextCount =
          nextItem.quantity /
          (currentItem.packageRelationQuantitySnapshot ?? 0);
        const priorCents = Math.round(
          currentItem.planProductSubtotalSnapshot * 100,
        );
        const expectedCents = (priorCents / priorCount) * nextCount;
        return (
          !Number.isSafeInteger(priorCents) ||
          priorCents % priorCount !== 0 ||
          !Number.isSafeInteger(expectedCents) ||
          nextItem.planProductSubtotalSnapshot !== expectedCents / 100
        );
      })
    ) {
      throw new Error('报价来源字段只读，未保存伪造变更');
    }
    const sourceDeviceSnapshot = currentItem.deviceFieldSnapshot
      ? (({ quoteLocalPricing: _local, ...snapshot }) => snapshot)(
          currentItem.deviceFieldSnapshot,
        )
      : undefined;
    const nextDeviceSnapshot = nextItem.deviceFieldSnapshot
      ? (({ quoteLocalPricing: _local, ...snapshot }) => snapshot)(
          nextItem.deviceFieldSnapshot,
        )
      : undefined;
    if (!same(sourceDeviceSnapshot, nextDeviceSnapshot)) {
      throw new Error('报价来源字段只读，未保存伪造变更');
    }
    if (
      !same(currentItem.formalPlanOccurrence, nextItem.formalPlanOccurrence) ||
      (currentItem.formalPlanOccurrence &&
        (updatedQuote.businessModeCode !== sourceQuote.businessModeCode ||
          currentItem.quantity !== nextItem.quantity))
    ) {
      const scaledOccurrence =
        validFragmentScale(currentItem) ||
        validContainerEdit(currentItem) ||
        (currentItem.formalPlanOccurrence &&
          isWholePlanQuantityChange(currentItem) &&
          same(
            {
              ...currentItem.formalPlanOccurrence,
              sourceLineCents: occurrenceSourceLineCents(nextItem),
            },
            nextItem.formalPlanOccurrence,
          ));
      if (
        !scaledOccurrence &&
        (updatedQuote.businessModeCode === sourceQuote.businessModeCode ||
          !same(
            repriceQuoteItem(currentItem, updatedQuote.businessModeCode)
              .formalPlanOccurrence,
            nextItem.formalPlanOccurrence,
          ))
      ) {
        throw new Error('方案实例只允许按所选模式重算，不可改写冻结来源');
      }
    }
    if (!same(currentItem.pricingBaseSnapshot, nextItem.pricingBaseSnapshot)) {
      if (
        updatedQuote.businessModeCode === sourceQuote.businessModeCode ||
        !same(
          repriceQuoteItem(currentItem, updatedQuote.businessModeCode)
            .pricingBaseSnapshot,
          nextItem.pricingBaseSnapshot,
        )
      ) {
        throw new Error('报价计价基础快照不可伪造');
      }
    }
    for (const [key, fieldId] of editableItemFields) {
      if (!same(currentItem[key], nextItem[key]) && !canEditSource(fieldId)) {
        throw new Error('报价核心字段编辑权限不足，未保存变更');
      }
    }
    assertQuoteQuantity(nextItem.quantity);
    if (
      currentItem.discount !== nextItem.discount &&
      !hasDiscountPercentPrecision(nextItem.discount)
    )
      throw new Error('折扣最多保留两位小数');
    assertDiscountPercent(nextItem.discount, '折扣');
  }
  const insertedFieldIds = [
    QUOTE_SOURCE_FIELDS.deviceId,
    QUOTE_SOURCE_FIELDS.name,
    QUOTE_SOURCE_FIELDS.sku,
    QUOTE_SOURCE_FIELDS.sourceType,
    QUOTE_SOURCE_FIELDS.sourceId,
    QUOTE_SOURCE_FIELDS.sourceName,
    QUOTE_SOURCE_FIELDS.quantity,
    QUOTE_SOURCE_FIELDS.discount,
    QUOTE_SOURCE_FIELDS.stock,
  ];
  for (const [itemId, nextItem] of nextItems) {
    if (currentItems.has(itemId)) continue;
    const pricingFieldId = `definition:${
      nextItem.pricingBaseSnapshot?.kind === 'rental'
        ? 'rental_price'
        : nextItem.pricingBaseSnapshot?.kind === 'retail-tax13'
          ? 'retail_price_tax13'
          : nextItem.pricingBaseSnapshot?.kind === 'buyout-tax13'
            ? 'purchase_final_price_tax13'
            : nextItem.pricingBaseSnapshot?.kind === 'package-item'
              ? 'package_item_price'
              : 'product_final_discount_price'
    }`;
    if (
      [...insertedFieldIds, pricingFieldId].some(
        (fieldId) => !canViewSource(fieldId),
      )
    ) {
      throw new Error('报价行字段查看上限不足，未新增设备行');
    }
    const insertedEditable = [
      QUOTE_SOURCE_FIELDS.quantity,
      QUOTE_SOURCE_FIELDS.discount,
      ...(nextItem.replacementDeviceId
        ? [QUOTE_SOURCE_FIELDS.replacementDeviceId]
        : []),
      ...(nextItem.remark ? [QUOTE_SOURCE_FIELDS.remark] : []),
    ];
    if (insertedEditable.some((fieldId) => !canEditSource(fieldId))) {
      throw new Error('报价行字段编辑上限不足，未新增设备行');
    }
    assertInsertedQuoteItemSource(
      state,
      nextItem,
      sourceQuote.businessModeCode,
      updatedQuote,
      sourceQuote,
    );
    assertQuoteQuantity(nextItem.quantity);
    if (!hasDiscountPercentPrecision(nextItem.discount))
      throw new Error('折扣最多保留两位小数');
    assertDiscountPercent(nextItem.discount, '折扣');
  }
  if (
    !same(sourceQuote.overallDiscount, updatedQuote.overallDiscount) &&
    !canEditSource(QUOTE_SOURCE_FIELDS.overallDiscount)
  ) {
    throw new Error('报价核心字段编辑权限不足，未保存整单折扣');
  }
  assertDiscountPercent(updatedQuote.overallDiscount, '整单折扣');
  if (
    sourceQuote.overallDiscount !== updatedQuote.overallDiscount &&
    !hasDiscountPercentPrecision(updatedQuote.overallDiscount)
  )
    throw new Error('整单折扣最多保留两位小数');
  if (!same(sourceQuote.customer, updatedQuote.customer)) {
    if (!canEditQuote) throw new Error('报价编辑权限不足，未保存客户变更');
    const customerFieldIds = [
      QUOTE_SOURCE_FIELDS.customerSnapshot,
      QUOTE_SOURCE_FIELDS.customerName,
      QUOTE_SOURCE_FIELDS.customerPhone,
    ];
    if (
      customerFieldIds.some(
        (fieldId) => !canViewSource(fieldId) || !canEditSource(fieldId),
      )
    ) {
      throw new Error('客户字段查看或编辑上限不足');
    }
    const customer = updatedQuote.customer.customerId
      ? state.catalog.customers.find(
          (item) => item.id === updatedQuote.customer.customerId,
        )
      : undefined;
    const unboundCustomerNameOnly =
      !updatedQuote.customer.customerId &&
      !updatedQuote.customer.managerUserId &&
      !updatedQuote.customer.customerNo &&
      !updatedQuote.customer.contact &&
      !updatedQuote.customer.phone;
    if (
      !unboundCustomerNameOnly &&
      (!customer ||
        !same(updatedQuote.customer, {
          customerId: customer.id,
          managerUserId: customer.managerUserId,
          name: customer.name,
          customerNo: customer.customerNo,
          contact: customer.contact,
          phone: customer.phone,
        }))
    )
      throw new Error('客户资源归属绑定无效');
  }
  const editableQuoteFields: Array<[keyof Quote, string]> = [
    ['validUntil', QUOTE_SOURCE_FIELDS.validUntil],
    ['formalQuoteExpiresAt', QUOTE_SOURCE_FIELDS.validUntil],
    ['deposit', QUOTE_SOURCE_FIELDS.deposit],
  ];
  for (const [key, fieldId] of editableQuoteFields) {
    if (!same(sourceQuote[key], updatedQuote[key]) && !canEditSource(fieldId)) {
      throw new Error('报价核心字段编辑权限不足，未保存变更');
    }
  }
  if (
    updatedQuote.businessModeCode !== sourceQuote.businessModeCode &&
    (!canEditQuote || !canEditSource(QUOTE_SOURCE_FIELDS.businessMode))
  ) {
    throw new Error('报价核心字段编辑权限不足，未保存业务模式');
  }
  // Permission denial precedes structural validation so denied injections
  // retain the same fail-closed contract and never gain an oracle on bindings.
  assertQuoteFieldMutationAllowed(
    state.role,
    sourceQuote,
    updatedQuote,
    state.catalog.quoteFieldDefinitions,
    state,
    authorize,
  );
  const currentSelections = quoteFieldSelections(sourceQuote);
  const nextSelections = quoteFieldSelections(updatedQuote);
  if (!same(currentSelections, nextSelections)) {
    const keys = nextSelections.map((selection) => selection.fieldKey);
    if (new Set(keys).size !== keys.length) {
      throw new Error('报价字段选择落点或标识重复，未保存变更');
    }
    const changedSelections = nextSelections.filter(
      (selection) =>
        !currentSelections.some((current) => same(current, selection)),
    );
    const pool = new Map(
      state.catalog.quoteFieldPool.map((entry) => [entry.fieldKey, entry]),
    );
    const mandatoryEntries = state.catalog.quoteFieldPool.filter(
      (entry) => entry.enabled && entry.mandatory,
    );
    if (
      mandatoryEntries.some(
        (entry) =>
          !nextSelections.some(
            (selection) =>
              selection.fieldKey === entry.fieldKey &&
              selection.placement === entry.placement,
          ),
      )
    ) {
      throw new Error('系统必备字段不可取消或改变落点');
    }
    if (
      changedSelections.some((selection) => {
        const entry = pool.get(selection.fieldKey);
        const definition = state.catalog.quoteFieldDefinitions.find(
          (field) => field.key === selection.fieldKey,
        );
        return (
          !entry?.enabled ||
          entry.placement !== selection.placement ||
          !definition?.enabled ||
          definition.governance.classification !== 'ordinary' ||
          !isQuoteDefinitionBound(selection.fieldKey)
        );
      })
    )
      throw new Error('报价字段落点或绑定无效，未保存变更');
    if (
      changedSelections.some(
        (selection) =>
          !canProjectGovernedQuoteField(
            state.role,
            sourceQuote,
            selection.fieldKey,
            state.catalog.quoteFieldDefinitions,
            state,
            authorize,
          ),
      )
    )
      throw new Error('字段权限不足，未保存本次字段选择');
  }
  let nextQuote: Quote = {
    ...updatedQuote,
    dirty: true,
    updatedAt: DEMO_TIME,
  };
  if (!same(currentSelections, nextSelections)) {
    const latest = bindLatestSelectedCalculations(state, nextQuote);
    const retained =
      sourceQuote.calculationBindings ??
      Object.fromEntries(
        (sourceQuote.customColumnSnapshot ?? []).flatMap((snapshot) =>
          snapshot.calculation
            ? [
                [
                  snapshot.key,
                  {
                    fieldKey: snapshot.key,
                    definitionVersion: snapshot.calculation.definitionVersion,
                    definition: structuredClone(snapshot.calculation),
                    fieldTypes: {},
                  } satisfies QuoteCalculationBinding,
                ],
              ]
            : [],
        ),
      );
    const newlySelected = new Set(
      nextSelections
        .filter(
          (selection) =>
            !currentSelections.some((current) => same(current, selection)),
        )
        .map((selection) => selection.fieldKey),
    );
    const merged: Record<string, QuoteCalculationBinding> = {};
    const addBindingClosure = (
      fieldKey: string,
      source: Record<string, QuoteCalculationBinding>,
      stack: readonly string[] = [],
    ) => {
      if (stack.includes(fieldKey))
        throw new Error('计算依赖循环，字段选择零写入');
      // Adding a new calculated column may depend on a calculation already
      // selected by this draft.  Keep that draft's frozen binding instead of
      // replacing it with the latest directory version as a side effect of
      // adding the dependent column.
      const alreadySelected = currentSelections.some(
        (selection) => selection.fieldKey === fieldKey,
      );
      const binding =
        (alreadySelected ? retained[fieldKey] : undefined) ?? source[fieldKey];
      if (!binding) return;
      const existing = merged[fieldKey];
      if (existing && JSON.stringify(existing) !== JSON.stringify(binding))
        throw new Error('新旧计算依赖版本冲突，请先显式应用最新公式版本');
      merged[fieldKey] = structuredClone(binding);
      for (const operand of binding.definition.operands) {
        if (operand.kind === 'field' && source[operand.fieldKey])
          addBindingClosure(operand.fieldKey, source, [...stack, fieldKey]);
      }
    };
    for (const selection of nextSelections) {
      const source = newlySelected.has(selection.fieldKey)
        ? (latest.calculationBindings ?? {})
        : retained;
      addBindingClosure(selection.fieldKey, source);
    }
    nextQuote = { ...latest, calculationBindings: merged };
  }
  next.quotes[quoteId] = nextQuote;
  return next;
}

export function saveDraft(state: CpqState, quoteId: string): CpqState {
  const sourceQuote = requireQuote(state, quoteId);
  if (sourceQuote.status !== 'draft') {
    throw new Error('非草稿报价只读，不能保存草稿');
  }
  const next = cloneState(state);
  const quote = requireQuote(next, quoteId);
  quote.dirty = false;
  quote.updatedAt = DEMO_TIME;
  if (quote.versionWorkflow?.stage === 'draft') {
    quote.versionWorkflow.savedAt = new Date().toISOString();
  }
  return appendAudit(next, 'draft.saved', 'success', '草稿已保存或更新', quote);
}

/** Explicitly apply the latest selected calculation definitions to a draft.
 * Existing drafts never switch versions implicitly; this command refreshes
 * the complete snapshot and calculation chain as one local transaction. */
export function applyLatestCalculationVersion(
  state: CpqState,
  quoteId: string,
  fieldValuePort: UnifiedFieldValuePort,
): CpqState {
  const source = requireQuote(state, quoteId);
  if (source.status !== 'draft')
    throw new Error('只有草稿报价可以应用新公式版本');
  const roleId = activeRoleIdForState(state);
  if (
    !roleId ||
    !resolveDisposablePermissionAction(
      state,
      roleId,
      'quotes',
      'quotes:quote.edit',
      'draft',
      quotePermissionContext(state, source),
    )
  )
    throw new Error('报价编辑权限不足，不能应用新公式版本');
  const next = cloneState(state);
  let quote = requireQuote(next, quoteId);
  const oldVersions = Object.fromEntries(
    Object.entries(quote.calculationBindings ?? {}).map(([key, binding]) => [
      key,
      binding.definitionVersion,
    ]),
  );
  const definitions = next.catalog.quoteFieldDefinitions;
  const definitionsByKey = new Map(
    definitions.map((definition) => [definition.key, definition]),
  );
  const pool = new Map(
    next.catalog.quoteFieldPool.map((entry) => [entry.fieldKey, entry]),
  );
  const selections = quoteFieldSelections(quote);
  quote.customColumnSnapshot = snapshotQuoteColumns(
    selections.map((selection) => selection.fieldKey),
    definitions,
    next.role,
    quote,
    next,
  ).map((snapshot) => {
    const entry = pool.get(snapshot.key);
    const definition = definitionsByKey.get(snapshot.key);
    return {
      ...snapshot,
      label: entry?.displayLabel ?? snapshot.label,
      options:
        definition?.options?.map((option) => ({ ...option })) ??
        snapshot.options,
      placement: entry?.placement ?? 'quote-line',
      calculation: snapshot.calculation
        ? structuredClone(snapshot.calculation)
        : undefined,
    };
  });
  quote = bindLatestSelectedCalculations(next, quote);
  const frozen = freezeSelectedCalculationFacts(next, quote, fieldValuePort);
  next.quotes[quoteId] = {
    ...frozen,
    dirty: true,
    updatedAt: DEMO_TIME,
  };
  return appendAudit(
    next,
    'quote.calculation.version.applied',
    'success',
    `已显式应用计算字段版本：${
      selectedCalculationKeys(frozen)
        .map(
          (key) =>
            `${key} ${oldVersions[key] ?? '未绑定'}→${frozen.calculationBindings?.[key]?.definitionVersion ?? '缺失'}`,
        )
        .join('、') || '无'
    }`,
    next.quotes[quoteId],
  );
}

export function markQuoteDirty(state: CpqState, quoteId: string): CpqState {
  const sourceQuote = requireQuote(state, quoteId);
  if (sourceQuote.status !== 'draft') {
    throw new Error('非草稿报价只读，不能修改报价');
  }
  const next = cloneState(state);
  const quote = requireQuote(next, quoteId);
  quote.dirty = true;
  quote.updatedAt = DEMO_TIME;
  quote.activeOperationId = undefined;
  return appendAudit(
    next,
    'quote.revised',
    'info',
    '失败后已修改，可重新处理',
    quote,
  );
}

export function transitionQuote(
  state: CpqState,
  quoteId: string,
  target: QuoteStatus,
  fieldValuePort: UnifiedFieldValuePort,
): CpqState {
  const next = cloneState(state);
  let quote = requireQuote(next, quoteId);
  if (
    quote.status === 'draft' &&
    target !== 'draft' &&
    quote.businessModeRequiresReselection
  ) {
    throw new Error('旧业务模式必须显式重选业务模式后才能提交');
  }
  if (target === 'draft' && quote.status !== 'draft') {
    throw new Error('不能将非草稿报价直接改回草稿');
  }
  if (!ALLOWED_TRANSITIONS[quote.status].includes(target)) {
    throw new Error(
      `不允许从${STATUS_LABELS[quote.status]}直接进入${STATUS_LABELS[target]}`,
    );
  }
  if (quote.status === 'draft' && target !== 'draft') {
    if (!fieldValuePort) throw new Error('离开草稿必须显式注入统一字段值端口');
    const definitions = new Map(
      next.catalog.quoteFieldDefinitions.map((field) => [field.key, field]),
    );
    const pool = new Map(
      next.catalog.quoteFieldPool.map((entry) => [entry.fieldKey, entry]),
    );
    for (const selection of quoteFieldSelections(quote)) {
      const key = selection.fieldKey;
      const definition = definitions.get(key);
      const entry = pool.get(key);
      const existingSnapshot = quote.customColumnSnapshot?.find(
        (item) =>
          item.key === key &&
          (item.placement ?? 'quote-line') === selection.placement,
      );
      const allowed =
        entry?.enabled &&
        entry.placement === selection.placement &&
        definition?.enabled &&
        definition.governance.classification === 'ordinary' &&
        canProjectGovernedQuoteField(
          next.role,
          quote,
          key,
          next.catalog.quoteFieldDefinitions,
          next,
        );
      // A historical snapshot is a read-only fact, never an authorization
      // grant for a current draft or a new version.
      if (!allowed) {
        throw new Error('字段池、来源或当前角色权限不足，拒绝冻结');
      }
      if (
        !existingSnapshot ||
        !definition ||
        existingSnapshot.type !== definition.type ||
        existingSnapshot.width !== definition.width ||
        existingSnapshot.sourceKind !== definition.sourceKind ||
        existingSnapshot.fieldKey !== definition.governance.fieldKey ||
        existingSnapshot.placement !== selection.placement
      ) {
        throw new Error('字段池、来源或当前角色权限不足，拒绝冻结');
      }
    }
    const currentSnapshots = snapshotQuoteColumns(
      quoteFieldSelections(quote).map((selection) => selection.fieldKey),
      next.catalog.quoteFieldDefinitions,
      next.role,
      quote,
      next,
    ).map((snapshot) => {
      const entry = pool.get(snapshot.key);
      const definition = definitions.get(snapshot.key);
      return entry &&
        definition &&
        entry.enabled &&
        definition.enabled &&
        definition.governance.classification === 'ordinary'
        ? {
            ...snapshot,
            label: entry.displayLabel,
            placement: entry.placement,
            fieldKey: definition.governance.fieldKey,
            definitionVersion: definition.governance.definitionVersion,
            formulaVersion: definition.governance.formulaVersion,
          }
        : snapshot;
    });
    quote.customColumnSnapshot = currentSnapshots.map((current) => {
      const bound = quote.customColumnSnapshot?.find(
        (snapshot) =>
          snapshot.key === current.key &&
          (snapshot.placement ?? 'quote-line') === current.placement,
      );
      return bound?.calculation
        ? {
            ...current,
            label: bound.label,
            definitionVersion: bound.definitionVersion,
            formulaVersion: bound.formulaVersion,
            calculation: structuredClone(bound.calculation),
            calculationSnapshot: bound.calculationSnapshot
              ? structuredClone(bound.calculationSnapshot)
              : undefined,
          }
        : current;
    });
    // Freeze selected/applicable calculation definitions, factor values and
    // results atomically before leaving draft. A thrown error leaves `state`
    // untouched and therefore blocks publication with zero writes.
    quote = freezeSelectedCalculationFacts(next, quote, fieldValuePort);
    quote = captureQuoteSourceFieldSnapshots(
      next,
      quote,
      quoteFieldSelections(quote),
      fieldValuePort,
    );
    next.quotes[quoteId] = quote;
  }
  quote.status = target;
  quote.updatedAt = DEMO_TIME;
  return appendAudit(
    next,
    'quote.status.changed',
    'success',
    `状态更新为${STATUS_LABELS[target]}`,
    quote,
  );
}

export function beginSubmission(
  state: CpqState,
  quoteId: string,
  fieldValuePort: UnifiedFieldValuePort,
): { state: CpqState; operationId: string } {
  let next = transitionQuote(state, quoteId, 'submitting', fieldValuePort);
  const quote = requireQuote(next, quoteId);
  const operationId = `OP-${String(next.nextSequence).padStart(4, '0')}`;
  next.nextSequence += 1;
  quote.activeOperationId = operationId;
  next = appendAudit(
    next,
    'submission.started',
    'info',
    `提交任务 ${operationId}`,
    quote,
  );
  return { state: next, operationId };
}

export function retrySubmission(
  state: CpqState,
  quoteId: string,
  fieldValuePort: UnifiedFieldValuePort,
): { state: CpqState; operationId: string } {
  const quote = requireQuote(state, quoteId);
  let prepared = state;
  if (quote.status === 'failed' || quote.status === 'draft') {
    prepared = transitionQuote(
      state,
      quoteId,
      'pending_submission',
      fieldValuePort,
    );
  }
  return beginSubmission(prepared, quoteId, fieldValuePort);
}

export function cancelSubmission(
  state: CpqState,
  quoteId: string,
  operationId: string,
): CpqState {
  const quote = requireQuote(state, quoteId);
  if (
    quote.status !== 'submitting' ||
    quote.activeOperationId !== operationId
  ) {
    return state;
  }
  const next = cloneState(state);
  const nextQuote = requireQuote(next, quoteId);
  nextQuote.status = 'pending_submission';
  nextQuote.activeOperationId = undefined;
  return appendAudit(
    next,
    'submission.canceled',
    'canceled',
    '已取消本次提交',
    nextQuote,
  );
}

export function applySubmissionResult(
  state: CpqState,
  quoteId: string,
  operationId: string,
  outcome: 'success' | 'failure',
): CpqState {
  const quote = state.quotes[quoteId];
  if (
    quote?.status !== 'submitting' ||
    quote.activeOperationId !== operationId
  ) {
    return state;
  }
  const next = cloneState(state);
  const nextQuote = requireQuote(next, quoteId);
  nextQuote.status = outcome === 'success' ? 'success' : 'failed';
  nextQuote.activeOperationId = undefined;
  nextQuote.dirty = false;
  if (outcome === 'success') {
    nextQuote.failureReason = undefined;
    nextQuote.salesOrderId = `20260825${String(next.nextSequence).padStart(8, '0').slice(-8)}`;
  } else {
    nextQuote.failureReason = 'MOCK-ERP-001：演示失败，请修改后重试';
    nextQuote.salesOrderId = undefined;
  }
  return appendAudit(
    next,
    'submission.completed',
    outcome,
    outcome === 'success'
      ? '模拟正式提交成功'
      : (nextQuote.failureReason ?? '模拟正式提交失败'),
    nextQuote,
  );
}
